import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type {
  BelowReservedRefusal,
  CountStockTakeResult,
  ReservationHolder,
  ClosedStockTakeRefusal,
  StaleStockLine,
  StockTakeBarcodeCheckInput,
  StockTakeDetail,
  StockTakeOrigin,
  StockTakeRound,
} from '@/infrastructure/FulfillmentAdminClient';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { StockClient } from '@/infrastructure/StockClient';
import { useAuth } from '@/providers/AuthProvider';
import { productAttributesClient, type BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import {
  ADJUST_REASONS,
  MISSING,
  UNASSIGNED_LINE,
  adjustReasonFor,
  auditTally,
  carryProductInfo,
  COUNT_ERROR,
  countInputError,
  countedDiff,
  describeHeldShelf,
  inLine,
  isValidCount,
  MAX_COUNT,
  inRoute,
  keepOrder,
  keyOf,
  locationLines,
  matchesSearch,
  mergeProductInfo,
  routeLabel,
  routesText,
  sortRows,
  sourcingRouteOptions,
  stockIntegritySummary,
  stockStatusCsv,
  auditCsv,
  shelfLabel,
  shelfNamedBy,
  skusNamedBy,
  isOversold,
  oversoldSkus,
  isUnassignedShelf,
  UNASSIGNED_SHELF,
  scannedBarcodeOf,
  withOnHand,
} from './inventoryView';
import type {
  AdjustReason,
  HeldShelf,
  InventoryScreenRow,
  InventoryTab,
  SortMode,
} from '@/pages/fulfillment/inventory/inventoryView';
import { AbandonAuditModal } from './components/AbandonAuditModal';
import { FinishAuditModal } from './components/FinishAuditModal';
import { WalkRowActions } from './components/WalkRowActions';
import type { ShelfLeft } from './components/FinishAuditModal';
import { MovementFormPane } from './components/MovementFormPane';
import { LocationImportModal } from './components/LocationImportModal';
import { NeedsFixingFirst } from './components/NeedsFixingFirst';
import { HelpTip } from '@/pages/fulfillment/components/HelpTip';
import {
  ExternalIcon,
  DownloadIcon,
  PencilIcon,
  RefreshIcon,
  UploadIcon,
} from '@/pages/fulfillment/components/UltimateIcons';
import { PastAuditLogsModal } from './components/PastAuditLogsModal';
import {
  RANDOM_CHECK_DEFAULT,
  RANDOM_CHECK_MAX,
  RECENTLY_CHECKED_DAYS,
  drawableShelves,
  isRandomCheck,
  lastCheckedByShelf,
  pagePassedWindow,
  randomCheckCount,
  randomCheckGroupId,
  randomShelfOrder,
  recentCutoff,
} from './randomCheck';
import { FullCountBand } from './components/FullCountBand';
import type { WalkStatus } from './components/FullCountBand';
import { FullCountProductPanel } from './components/FullCountProductPanel';
import type { ShelfCandidate } from './components/FullCountProductPanel';
import { searchProductPicks } from '@/pages/fulfillment/components/productPick';
import { listEveryOpenRound } from '@/pages/fulfillment/components/openRounds';
import {
  VERDICT_LABELS,
  barcodelessCount,
  fullCountCsv,
  fullCountGroupId,
  isFullCount,
  resumeShelf,
  rowsNamedOnShelf,
  stepShelf,
  verdictOf,
  annotationsOfLine,
  walkCountOf,
  walkHistory,
  walkShelves,
  walkSummary,
} from './fullCountWalk';
import type { AppliedWalkCount, FullCountVerdict } from './fullCountWalk';
import { ReservedOrdersModal } from './components/ReservedOrdersModal';
import type { ReleaseDraft } from './components/ReservedOrdersModal';
import { StockAdjustPopover } from './components/StockAdjustPopover';
import type { StockAdjustConflict } from './components/StockAdjustPopover';
import { StockHistoryPane } from './components/StockHistoryPane';
import { LocationCell } from './components/LocationCell';
import { LocationStockCard } from './components/LocationStockCard';
import { findInAddress, historySearchInAddress, inventoryAddress, tabInAddress, withoutFind } from './inventoryAddress';
import { reservationsMovedText } from './locationImport';
import type { StockHistoryAddress } from './inventoryAddress';
import { BarcodeCell } from '@/pages/fulfillment/components/BarcodeCell';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { AUTO_DISMISS, useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import {
  belowReservedRefusalOf,
  closedStockTakeRefusalOf,
  describeClosedStockTake,
  describeFailure,
  describeStaleStock,
  isRoundAlreadyClosed,
  staleStockRefusalOf,
} from '@/utils/fulfillmentErrors';
import { type BulkItemReason, interruptedSequenceResult } from '@/pages/fulfillment/components/bulkResult';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';
import { SCAN_CURSOR_OWNERS, useScanInput } from '@/pages/fulfillment/components/useScanInput';
import { lookupProductsInChunks, readEveryInventoryPage, readMissingTargets } from './inventoryRead';
import '../wireframe.css';
import '../ultimateDesign.css';
import './inventory.css';

const GENERIC_ERROR = 'Could not load the stock. Please try again shortly.';

/** Column widths that keep the whole table inside the viewport; the image column is the one fixed-pixel column (a 32px thumbnail plus the cell's 8px sides). */
const BROWSE_COLUMN_WIDTHS: Array<[string, string]> = [
  ['sku', '10%'],
  ['image', '48px'],
  ['name', '16%'],
  ['name-kr', '14%'],
  ['size', '6%'],
  ['barcode', '12%'],
  ['route', '8%'],
  ['location', '9%'],
  ['total', '8%'],
  ['reserved', '6%'],
  ['available', '6%'],
];
const AUDIT_COLUMN_WIDTHS: Array<[string, string]> = [
  ['sku', '9%'],
  ['image', '48px'],
  ['name', '10%'],
  ['name-kr', '9%'],
  ['size', '5%'],
  ['barcode', '10%'],
  ['route', '7%'],
  ['location', '8%'],
  ['total', '7%'],
  ['reserved', '6%'],
  ['available', '6%'],
  // Wide enough for the notes a live count leaves under its box: a refusal, a short-count tick, WH#00, what was saved.
  ['counted', '11%'],
  ['diff', '5%'],
];

/**
 * Beyond the controls, what keeps the cursor on this page: the ✎ and Location boxes, the (?) panels and the
 * dialogs, so the search box does not take the cursor out of something the desk is working in [SSD-7529].
 */
const INVENTORY_CURSOR_OWNERS = `${SCAN_CURSOR_OWNERS}, .tot-pop, [role="note"], .modal`;

/**
 * How often a live audit reads the stock again [SSD-7447]: inbound and outbound keep running while the floor counts, so
 * the book on screen is never more than this old. Only quantities move; the catalog is read for new skus alone.
 */
const AUDIT_REFRESH_MS = 20000;

const countStateKey = (docId: string, rowKey: string): string => `${docId}\u0000${rowKey}`;

/**
 * How long ✎, moves and Start wait for the standing-walk read before they are offered anyway. The server still refuses
 * a held shelf, now in plain words, so a read that never answers costs a refusal rather than a page locked for good.
 */
const RECOVERY_LOCK_MS = 15000;

/** How many rounds the audit log reads per page; older ones come on request [SSD-7212]. */
const ROUNDS_PAGE_SIZE = 50;

/** A Random check reads confirmed rounds this many at a time, the most the server serves in one page [SSD-7583]. */
const RANDOM_CHECK_HISTORY_PAGE_SIZE = 200;

/** The most pages a Random check reads to find what was counted recently; two weeks of counts fit well inside. */
const RANDOM_CHECK_HISTORY_PAGES = 15;

/** The round a 409 STOCK_TAKE_OPEN names, and who holds it; the server calls the round `docId` everywhere [D-n]. */
interface StandingRound {
  docId: string;
  operator: string | null;
  auditGroupId: string | null;
}

/** The round a shelf already has open, when that is what a 409 is saying [D-n]. */
const standingRoundOf = (failure: unknown): StandingRound | null => {
  const body = axios.isAxiosError<{
    code?: string;
    docId?: string;
    operator?: string | null;
    auditGroupId?: string | null;
  }>(failure)
    ? failure.response?.data
    : undefined;
  return body?.code === 'STOCK_TAKE_OPEN' && body.docId
    ? { docId: body.docId, operator: body.operator ?? null, auditGroupId: body.auditGroupId ?? null }
    : null;
};

/** The round a 409 STOCK_TAKE_HELD says holds the shelf, one this request could never join [SSD-7472]. */
const heldShelfOf = (failure: unknown): HeldShelf | null => {
  const body = axios.isAxiosError<{
    code?: string;
    locationCode?: string | null;
    operator?: string | null;
    origin?: string | null;
    startedAt?: string | null;
  }>(failure)
    ? failure.response?.data
    : undefined;
  return body?.code === 'STOCK_TAKE_HELD'
    ? {
        locationCode: body.locationCode ?? null,
        operator: body.operator ?? null,
        origin: body.origin ?? null,
        startedAt: body.startedAt ?? null,
      }
    : null;
};

/**
 * Whether a round was opened by the person at this keyboard. Once request attribution is available,
 * the server credits a round to that handle and it is the only value that can match; until then, no
 * shared-system round is ours.
 */
const isOwnRound = (operator: string | null | undefined): boolean => {
  const self = OperatorIdentity.current();
  return Boolean(self && operator && operator.trim().toLowerCase() === self);
};

/**
 * The rounds a refresh should pick back up: the operator's own most recent walk and nothing else.
 * Another worker's round is theirs to finish, and a round with no walk (a ✎ edit, or one left by the
 * retired Stock Take screen) is closed from Past Audit Logs, so neither is ever folded into this walk.
 * The list comes newest first.
 */
const walkToResume = (rounds: StockTakeRound[]): StockTakeRound[] => {
  const own = rounds.filter((round) => round.status === 'OPEN' && round.auditGroupId && isOwnRound(round.operator));
  const auditGroupId = own[0]?.auditGroupId;
  return auditGroupId ? own.filter((round) => round.auditGroupId === auditGroupId) : [];
};

/**
 * The skus a round froze when it opened, read off the round document the detail endpoint returns.
 * Only a stored array is a frozen set; anything else means the round never recorded one.
 */
const frozenTargetsOf = (detail: StockTakeDetail): string[] | null => {
  const meta = detail.doc?.['meta'] as { targetSkus?: unknown } | undefined;
  return Array.isArray(meta?.targetSkus) && meta.targetSkus.every((sku) => typeof sku === 'string')
    ? (meta.targetSkus as string[])
    : null;
};

/** What an audit is, what it covers and what it locks, behind the (?) on the audit banner [SSD-7472, SSD-7447]. */
const AUDIT_HELP_LINES = [
  'Start Stock Audit opens a count on every shelf of the rows that Line, Sourcing route and Show zero-quantity rows keep. To count one shelf, scan its label into the search and press Count this shelf. During an audit a scanned shelf that is not in it can be added with Add this shelf. The search does not narrow the audit: it only finds rows on screen.',
  'Type what is physically on the shelf into Counted Qty and press Enter, or leave the box: the difference goes into the stock at once, as an ADJUST in Stock History. Enter on a box still showing the book value says the shelf matches. Inbound and outbound keep running while you count.',
  'Each row is counted against the stock it held when you started typing in it. If that stock moved before you saved, the row is refused and cleared: count it again. The list reads the stock again every 20 seconds, and a row that moved after it was counted says by how much.',
  'A count above the book takes the extra units from WH#00 first when WH#00 holds that product, with the orders reserving them; untick Take from WH#00 to add them instead. A product found on a shelf that has no row for it is added by scanning it after the shelf label.',
  'A count that differs from the book is saved only once you pick why it differs (Damaged, Expired, Lost or Miscount), and the reason shows on its ADJUST in Stock History. A count equal to the book needs none.',
  'A count below the reserved quantity is saved only once you pick a reason and tick that you checked the orders going short, the same rule as the ✎ box.',
  'Scan a shelf label into the search to list that shelf alone (B1 does not bring B11). Enter goes to its first Counted Qty, each Enter to the next row, and Enter on the last row back to the search for the next shelf.',
  'Finish Audit closes the shelves and lists the rows nobody counted, which keep the stock the book shows. Exit Stock Audit closes them as abandoned; counts already saved stay in the stock either way.',
  `Random check opens a count on the number of shelves in its box (10 to start), drawn at random from what Line, Sourcing route and Show zero-quantity rows keep. Shelves an audit confirmed in the last ${RECENTLY_CHECKED_DAYS} days are drawn only when there are not enough others, and a shelf another round holds is skipped. During a Random check the button adds that many more. At Finish a drawn shelf with nothing counted closes as not checked, so the next draw still picks it first. Past Audit Logs lists the walk as a Random check.`,
  'While the audit is open its shelves are locked: ✎ and Location moves on them wait until it is finished or exited. Rows under Needs fixing first are left out.',
  'Full count walk opens the same shelves for a count of the whole warehouse, shelf by shelf: every box starts blank, each product is found by scanning it, and Enter goes back to the scan box. Its band says where you are and steps with Previous and Next (F7, F8).',
];

/** The status chip each full count verdict wears; the colours are the screen's own st-* states [SSD-7662]. */
const VERDICT_CLASS: Record<FullCountVerdict, string> = {
  'not-counted': 'fc-v-none',
  'barcode-unchecked': 'st-hold',
  'barcode-correction': 'st-hold',
  'moved-out': 'st-cancelled',
  matches: 'st-processing',
  over: 'st-hold',
  short: 'st-hold',
  'not-on-shelf': 'st-cancelled',
  added: 'st-completed',
  'added-not-counted': 'st-completed',
};

/** A product the catalog names but the book holds nowhere, as a row the walk can count on a shelf at 0 [SSD-7662]. */
const catalogRow = (candidate: ShelfCandidate, locationCode: string): InventoryScreenRow => ({
  sku: candidate.sku,
  locationCode,
  onHand: 0,
  reserved: 0,
  available: 0,
  ...(candidate.name !== null ? { name: candidate.name } : {}),
  ...(candidate.nameKor !== null ? { nameKor: candidate.nameKor } : {}),
  ...(candidate.brand !== null ? { brand: candidate.brand } : {}),
  ...(candidate.size !== null ? { size: candidate.size } : {}),
  ...(candidate.barcode !== null ? { barcode: candidate.barcode } : {}),
  ...(candidate.imageUrl !== null ? { imageUrl: candidate.imageUrl } : {}),
});

/** Why a round closed without applying anything — the server keeps the walk on record either way. */
const ABANDON_REASON = 'stock audit exited';

/** Why a Random check shelf closed at Finish with nothing counted on it [SSD-7583]. */
const RANDOM_NOT_CHECKED_REASON = 'random check: shelf not counted';

/** Why a ✎ adjustment's own round was closed after the server refused its count or confirm. */
const ADJUST_REFUSED_REASON = 'stock adjustment refused';

/** One ✎ correction, with the balance its editor opened against so a stale save is refused. */
interface AdjustmentEntry {
  row: InventoryScreenRow;
  countedQty: number;
  diff: number;
  expectedSystemQty: number;
  reason: AdjustReason | null;
  /** The tick on a count below the reserved quantity [SSD-7437 3·4]. */
  acknowledgeShortfall: boolean;
  /** The balance version the editor opened on, when the server reports one [SSD-7447]. */
  expectedVersion: number | undefined;
}

/** One audit row already written to the stock: the shelf held `countedQty` right after, and the gaps written add to `delta`. */
interface AppliedCount
  extends Pick<AppliedWalkCount, 'barcodeCheck' | 'note' | 'barcodeCorrection' | 'movedFrom' | 'movedTo' | 'movedQty'> {
  countedQty: number;
  delta: number;
  movedFromUnassigned: number;
  at: string | null;
  /** The shelf's balance version right after the write; a later movement shows as a newer one. */
  versionAfter?: number;
}

/** The book a row was frozen at when counting it began: the save is refused for that row alone once it has moved [SSD-7447]. */
interface FrozenBook {
  onHand: number;
  version?: number;
}

/** The row carrying the balance version the server last reported, or none when it is not known. */
const withVersion = (row: InventoryScreenRow, version: number | undefined): InventoryScreenRow => {
  const next = { ...row };
  if (version === undefined) {
    delete next.version;
  } else {
    next.version = version;
  }
  return next;
};

/** The book a row is frozen at: its quantity and, when the server reports one, its balance version. */
const bookOf = (row: InventoryScreenRow): FrozenBook =>
  row.version !== undefined ? { onHand: row.onHand, version: row.version } : { onHand: row.onHand };

/** Carries how many shelves were already confirmed when a later one was refused. */
class PartialAuditFailure extends Error {
  public constructor(
    public readonly confirmed: number,
    public readonly total: number,
    public readonly cause: unknown,
  ) {
    super('The audit was only partly recorded.');
  }
}

/**
 * Renders inventory browsing, audits, reservations, and stock movement forms.
 * Reports operation results through shared fulfillment notifications, keeping
 * failures visible until dismissed while successful notifications leave 6 s after they can be read (SSD-7705).
 */
export default function FulfillmentInventoryPage() {
  const adminClient = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const stockClient = useMemo(() => new StockClient(), []);
  const { user } = useAuth();
  const operator = user?.name || user?.email || 'admin';

  const { toasts, showSuccess, showError, showWarning, showFailure, dismiss } = useFulfillmentToast();

  // The tab and a Stock History search live in the address, so a reload, Back or a link lands on them [SSD-7444].
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabInAddress(searchParams);
  const historySearch = useMemo(() => historySearchInAddress(searchParams), [searchParams]);
  const showInAddress = (nextTab: InventoryTab, search: StockHistoryAddress | null) => {
    const next = inventoryAddress(searchParams, nextTab, search);
    // The same address again would only stack a duplicate entry under Back.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next);
    }
  };
  const [rows, setRows] = useState<InventoryScreenRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A term the address carried (a link from the Manual Stock warning) is in the box from the first paint and is
  // dropped from the address at once, so Back or a reload does not search it again [SSD-7527].
  const [query, setQuery] = useState(() => findInAddress(searchParams));
  useEffect(() => {
    const next = withoutFind(searchParams);
    if (next) {
      // A link from inside the page (the Manual Stock warning) arrives on a mounted screen, so the term is set here too.
      setQuery(findInAddress(searchParams));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [line, setLine] = useState('all');
  const [route, setRoute] = useState('all');
  const [showZeroQty, setShowZeroQty] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('available-desc');

  /** The shelf whose ✎ box is open; one box on the screen at a time, and never during an audit [SSD-7456]. */
  const [editingShelf, setEditingShelf] = useState<string | null>(null);
  /** Moves still waiting on the server; an audit cannot open over a shelf that is still moving. */
  const [movesInFlight, setMovesInFlight] = useState(0);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  /** The ✎ box open on the fix-first list. Opening it closes the stock table's and the reverse, so one row never opens two. */
  const [fixing, setFixing] = useState<string | null>(null);
  const [adjustBusy, setAdjustBusy] = useState(false);
  /** The open ✎ box whose save was refused because the shelf moved under it; it stays open with the typed count. */
  const [adjustConflict, setAdjustConflict] = useState<(StockAdjustConflict & { key: string }) | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  /** Bumped by Refresh, so the order a ✎ save held is let go [SSD-7449]. */
  const [sortEpoch, setSortEpoch] = useState(0);
  /** The order on screen when a ✎ save landed, the view it was shown for and the row saved [SSD-7449]. */
  const [heldOrder, setHeldOrder] = useState<{ view: string; keys: string[]; key: string } | null>(null);
  /** The row order of the last committed render, recorded after commit so a discarded render never counts. */
  const committedOrderRef = useRef<string[]>([]);

  const [auditOn, setAuditOn] = useState(false);
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [finishingAudit, setFinishingAudit] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  /** The page is still reading whether a walk of the operator's is standing on the server [SSD-7472]. */
  const [recoveringWalk, setRecoveringWalk] = useState(true);
  const finishingAuditRef = useRef(false);
  /** Audit rows written to the stock in this walk, keyed like the screen's rows [SSD-7447]. */
  const [applied, setApplied] = useState<Record<string, AppliedCount>>({});
  /** Counts the server refused for going below the reserved quantity, waiting for the tick [SSD-7437 3·4]. */
  const [shortfallPrompts, setShortfallPrompts] = useState<Record<string, BelowReservedRefusal>>({});
  /** The reason picked for a short count; ✎ and the audit share one rule, a reason and the tick [SSD-7437 3·4]. */
  const [shortfallReasons, setShortfallReasons] = useState<Record<string, AdjustReason>>({});
  /** Counts that differ from the book, held back until the counter picks why [SSD-7591]. */
  const [reasonPrompts, setReasonPrompts] = useState<Record<string, { countedQty: number; book: number }>>({});
  /** Rows whose Take from WH#00 was unticked, so the extra units are added rather than moved [SSD-7447]. */
  const [keepUnassigned, setKeepUnassigned] = useState<Record<string, boolean>>({});
  /** The shelf last scanned during the walk, which a product scanned next is found on [SSD-7447]. */
  const [countingShelf, setCountingShelf] = useState<string | null>(null);
  /** Ties every shelf of one walk together, so a refresh finds the whole session rather than one round. */
  const [auditGroupId, setAuditGroupId] = useState(() => uuidv4());
  /** How many shelves Random check draws; the center counts ten a day and sometimes more [SSD-7583]. */
  const [randomCount, setRandomCount] = useState(String(RANDOM_CHECK_DEFAULT));
  const [exiting, setExiting] = useState(false);
  /** The full count walk's last word under its band [SSD-7662]. */
  const [walkStatus, setWalkStatus] = useState<WalkStatus | null>(null);
  /** The rows of the shelf a scanned code names when it names more than one, waiting for the counter's pick. */
  const [codeChoice, setCodeChoice] = useState<string[] | null>(null);
  /** The walk row whose More panel is open [SSD-7662]. */
  const [moreRow, setMoreRow] = useState<string | null>(null);
  /** A scanned code that names no row of the shelf, with the shelf it was scanned on, for the product search. */
  const [productLookup, setProductLookup] = useState<{ code: string; shelf: string } | null>(null);
  const [walkHistoryOpen, setWalkHistoryOpen] = useState(false);
  /** When the walk opened; after a refresh, the earliest of its rounds. */
  const [walkStartedAt, setWalkStartedAt] = useState<string | null>(null);
  /** Rows added to a shelf during the walk, so the verdict and the export say so after a refresh too. */
  const [walkFound, setWalkFound] = useState<Record<string, true>>({});
  // locationCode → the open round the server holds for that shelf; the walk is the server's, not the tab's [D-n].
  const [openRoundsByShelf, setOpenRoundsByShelf] = useState<Record<string, string>>({});
  const [auditTargetsByShelf, setAuditTargetsByShelf] = useState<Record<string, string[]>>({});
  const [rounds, setRounds] = useState<StockTakeRound[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [showRounds, setShowRounds] = useState(false);
  /** Where the next page of the audit log starts; null once it has been read to its end. */
  const [roundsCursor, setRoundsCursor] = useState<string | null>(null);
  /** Bumped when the audit log starts a new listing, so a page that answers for an older one is ignored. */
  const roundsListingRef = useRef(0);
  const [roundsOrigin, setRoundsOrigin] = useState<StockTakeOrigin | 'all'>('all');
  const [importingLocations, setImportingLocations] = useState(false);

  const [reservedSku, setReservedSku] = useState<string | null>(null);
  const [reservedLocation, setReservedLocation] = useState('');
  const [holders, setHolders] = useState<ReservationHolder[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [holdersFailed, setHoldersFailed] = useState(false);
  const [releaseDraft, setReleaseDraft] = useState<ReleaseDraft>({ reason: '', restock: false, restockQty: '' });
  const [releaseBusy, setReleaseBusy] = useState<string | null>(null);
  const relocatingRef = useRef(new Set<string>());
  const releaseInFlightRef = useRef(false);
  const exportInFlightRef = useRef(false);
  const adjustInFlightRef = useRef(false);
  const shelfKeysRef = useRef(new Map<string, string>());
  const reservedRequestRef = useRef(0);
  const reservedSkuRef = useRef<string | null>(null);
  const loadRequestRef = useRef(0);
  const recoveryRequestRef = useRef(0);
  /** What the server was last told for each counted line, so a flush never re-sends an unchanged count. */
  const persistedRef = useRef<Record<string, string>>({});
  const savedCountsRef = useRef<Record<string, string>>({});
  const countedRef = useRef<Record<string, string>>({});
  const countWriteQueuesRef = useRef<Record<string, Promise<void>>>({});
  const scheduledCountsRef = useRef<Record<string, string>>({});
  const countSaveFailuresRef = useRef<Record<string, unknown>>({});
  /** Rows whose count or confirm was refused because their shelf moved; each is recounted before the next confirm. */
  const [staleRecounts, setStaleRecounts] = useState<Record<string, StaleStockLine>>({});
  /**
   * Rounds this walk no longer writes to: confirmed or closed here, or closed by a colleague [SSD-7209]. Read
   * synchronously, so a second refused write on the same shelf says nothing new, a queued write is not sent to it,
   * and two shelves refused in the same flush each see the other one gone.
   */
  const closedRoundsRef = useRef<Set<string>>(new Set());
  /** Stale holds a typed number lifted; a box emptied again before that count is written puts its hold back. */
  const liftedStaleRef = useRef<Record<string, StaleStockLine>>({});
  /** The book value each untouched line was prefilled with; it follows the book until the floor enters a count. */
  const prefilledRef = useRef<Record<string, string>>({});
  /** Per line, the book frozen when counting it began; dropped once the count is in the stock [SSD-7447]. */
  const frozenRef = useRef<Record<string, FrozenBook>>({});
  /** Per line, the retry key of the value being saved: the same value again replays, a new value is a new write. */
  const applyKeysRef = useRef<Record<string, { value: string; key: string }>>({});
  /** Lines whose short count was ticked, with the reason picked for it, so the save goes through with the acknowledgement. */
  const shortfallAckRef = useRef<Record<string, AdjustReason>>({});
  /** The reason picked for a count that differs from the book, so its save carries it to the ADJUST [SSD-7591]. */
  const countReasonRef = useRef<Record<string, AdjustReason>>({});
  /**
   * Rows the full count walk was told to count again: the next save is the row's count against the stock as it stands,
   * not a second gap on top of the first, which is how the round reads it back after a refresh [SSD-7662].
   */
  const recountedRef = useRef<Set<string>>(new Set());
  /** When a key last went into a reason box: a reason picked within a moment of it was typed, not clicked [SSD-7662]. */
  const reasonKeyAtRef = useRef<{ key: string; at: number } | null>(null);
  /**
   * In a full count walk, a reason being typed into its box: picked on the first letter but kept in the box until Enter or
   * leaving the box, so the rest of the word stays there instead of landing in the scan box [SSD-7662].
   */
  const typedReasonsRef = useRef<Record<string, AdjustReason>>({});
  const [typedReasons, setTypedReasons] = useState<Record<string, AdjustReason>>({});
  /** Rows added because the counter found the product on a shelf the book has no row for; kept until the server has one. */
  const foundRowsRef = useRef<InventoryScreenRow[]>([]);
  /** A row the screen should put the cursor in once it is drawn (a found product, a shelf just opened). */
  const focusRowRef = useRef<string | null>(null);
  /** Reads still out; the 20-second refresh skips its beat rather than stacking reads. */
  const loadsOutRef = useRef(0);
  /** The rows on screen, for writes that run from callbacks made before the latest read. */
  const rowsRef = useRef<InventoryScreenRow[]>([]);
  /** Lines added as found on their shelf, so their save says so [SSD-7447]. */
  const foundRef = useRef<Record<string, boolean>>({});
  /**
   * The 88 code that found a row in a full count walk, waiting to travel with its count [SSD-7662]. A row already counted
   * takes it at once through annotate instead, since annotate only writes on a counted line.
   */
  const scanCheckRef = useRef<Record<string, StockTakeBarcodeCheckInput>>({});
  /** The code a multi-product scan was made with, so the row picked from its choice is checked with it. */
  const choiceCodeRef = useRef<string | null>(null);
  const keepUnassignedRef = useRef<Record<string, boolean>>({});
  /** Read by `load`, which runs from listeners that would otherwise see a stale `auditOn`. */
  const auditOnRef = useRef(false);
  auditOnRef.current = auditOn;
  /** The walk's targets, read by `load` from listeners that would otherwise see a stale map. */
  const auditTargetsRef = useRef<Record<string, string[]>>({});
  auditTargetsRef.current = auditTargetsByShelf;
  /**
   * The search box takes the cursor back the way View Orders' scan box does [SSD-6928, SSD-7529]: a leftover is selected
   * so the next scan replaces it, and a code still arriving is never cut.
   */
  const searchInput = useScanInput({ preventScroll: true });
  /** A scanner's Enter that landed while the Korean IME was still composing; it is acted on once composing ends. */
  const searchEnterOwedRef = useRef(false);
  const countInputsRef = useRef<Array<HTMLInputElement | null>>([]);
  countedRef.current = counted;
  rowsRef.current = rows;
  keepUnassignedRef.current = keepUnassigned;

  // A refusal belongs to the box it was shown in; opening or closing any box starts clean.
  useEffect(() => {
    setAdjustConflict(null);
  }, [adjusting, fixing]);

  // An audit locks every shelf code, so a box left open when it starts must not reopen when it ends [SSD-7456].
  useEffect(() => {
    if (auditOn) {
      setEditingShelf(null);
    }
  }, [auditOn]);

  // Current Stocks opens with the cursor in the search box, as View Orders opens in its scan box, so the first shelf
  // label scanned lands there [SSD-7529].
  useEffect(() => {
    if (tab === 'current') {
      searchInput.reclaim();
    }
  }, [tab, searchInput]);

  // A click anywhere on Current Stocks that is not a control or a box being worked in hands the cursor back to the
  // search, the leftover selected, so the next scan replaces it; a drag that selected text is someone copying [SSD-7529].
  useEffect(() => {
    if (tab !== 'current') {
      return undefined;
    }
    const handBack = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest(INVENTORY_CURSOR_OWNERS)) {
        return;
      }
      if ((window.getSelection()?.toString() ?? '') !== '') {
        return;
      }
      searchInput.reclaim();
    };
    document.addEventListener('click', handBack);
    return () => document.removeEventListener('click', handBack);
  }, [tab, searchInput]);

  /** A read already in flight predates a local patch, so it must not land over it. */
  const invalidateInventoryReads = () => {
    loadRequestRef.current += 1;
    setLoading(false);
  };

  /**
   * Answers the rows it just loaded, so a caller can decide whether its own filter still points anywhere;
   * null when a newer read or a local patch has taken the screen since.
   */
  const load = async (includeEmpty: boolean, saved?: string, quiet = false): Promise<InventoryScreenRow[] | null> => {
    const request = ++loadRequestRef.current;
    // The audit's 20-second read is quiet: the table keeps working while it reads, and a failed beat says nothing [SSD-7447].
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    loadsOutRef.current += 1;
    try {
      const list = (params: Parameters<FulfillmentAdminClient['listInventory']>[0]) =>
        adminClient.listInventory(params);
      const stillWanted = () => request === loadRequestRef.current;
      // Every page, not the first 500 rows by location: the shelves after them were never on screen [SSD-7529].
      const read = await readEveryInventoryPage(list, includeEmpty ? { includeEmpty: true } : {}, stillWanted);
      // A frozen target can sit at zero, and hiding it mid-walk would drop it from the audit; only those are read back,
      // not every zero row in the warehouse [SSD-7529].
      const frozen =
        read !== null && auditOnRef.current && !includeEmpty
          ? await readMissingTargets(list, read, auditTargetsRef.current, stillWanted)
          : [];
      // A newer read owns the screen now; this one still answered.
      if (read === null || frozen === null || request !== loadRequestRef.current) {
        if (saved) {
          showSuccess(saved);
        }
        return null;
      }
      const listed = [...read, ...frozen];
      // A product found on a shelf has no balance there until its count is saved, so it stays a row of the walk until
      // the server lists it [SSD-7447].
      foundRowsRef.current = foundRowsRef.current.filter((found) => !listed.some((row) => keyOf(row) === keyOf(found)));
      const result = { rows: [...listed, ...foundRowsRef.current] };
      // A tab coming back, a move or a save re-reads the whole list; the details already on screen stay up until the
      // catalog answers, or every row would flash to a placeholder and a name search would empty the table [SSD-7461].
      setRows((previous) => carryProductInfo(result.rows, previous));
      // The quiet audit read looks up only skus the screen has no details for yet: the rest are carried over.
      const detailed = new Set(
        rowsRef.current.filter((row) => row.name !== undefined || row.nameKor !== undefined).map((row) => row.sku),
      );
      const skus = [...new Set(result.rows.map((row) => row.sku))].filter((sku) => !quiet || !detailed.has(sku));
      if (skus.length > 0) {
        // The stock endpoint carries quantities only; name, size, barcode and image come from the catalog.
        // It has to be the admin lookup: the customer-facing one sanitizes nameKor and the barcode away.
        try {
          // In chunks: the skus ride in the address, and every page's worth of them would outgrow it [SSD-7529].
          const products = await lookupProductsInChunks((chunk) => stockClient.getAdminProductsBySkus(chunk), skus);
          if (request === loadRequestRef.current) {
            setRows((previous) =>
              quiet
                ? mergeProductInfo(carryProductInfo(result.rows, previous), products)
                : mergeProductInfo(result.rows, products),
            );
          }
        } catch {
          // A catalog outage must not blank the quantities the warehouse came here for; the last details loaded stay up.
          if (request === loadRequestRef.current && !quiet) {
            showError('Product names and images could not be updated right now.');
          }
        }
      }
      if (saved) {
        showSuccess(saved);
      }
      return result.rows;
    } catch {
      if (request !== loadRequestRef.current) {
        if (saved) {
          showSuccess(saved);
        }
        return null;
      }
      if (quiet) {
        // The next beat reads again; a missed one is not worth a banner over the table.
        return null;
      }
      if (saved) {
        showWarning(`${saved} Could not refresh inventory; reload before taking another action.`);
      }
      setError(GENERIC_ERROR);
      return [];
    } finally {
      loadsOutRef.current -= 1;
      if (request === loadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  /**
   * A barcode typed here is saved against the product master, not this shelf — the same gap follows
   * the sku onto every screen, so closing it once closes it everywhere. A refusal leaves the input up.
   */
  const saveBarcode = async (productId: number, barcode: string): Promise<BarcodeOwner | undefined> => {
    const result = await productAttributesClient.updateBarcode(productId, barcode);
    if (!result.success) {
      // Another product carries the code: the cell shows which one under the box (SSD-7532).
      if (result.owner) {
        return result.owner;
      }
      showError(result.message);
      return undefined;
    }
    invalidateInventoryReads();
    setRows((previous) => previous.map((row) => (row.productId === productId ? { ...row, barcode } : row)));
    showSuccess(`✓ Barcode saved — ${barcode}`);
    return undefined;
  };

  // The server withholds rows at zero, so revealing them is a new request, not a filter over what we hold.
  useEffect(() => {
    void load(showZeroQty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showZeroQty]);

  // Another desk moves stock all day, so a tab coming back to the front is looking at a stale book [D-p].
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void load(showZeroQty);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showZeroQty]);

  /**
   * A walk lives on the server, so a refresh picks the standing rounds and their counts back up [D-n]. Until that read
   * settles the page cannot tell which shelves are held, so ✎, moves and Start Stock Audit wait for it, and the rounds
   * are read side by side: a walk over the whole stock is dozens of shelves [SSD-7472].
   */
  const recoverWalk = async () => {
    const recoveryRequest = ++recoveryRequestRef.current;
    setRecoveringWalk(true);
    const giveUp = window.setTimeout(() => {
      if (recoveryRequest === recoveryRequestRef.current) {
        setRecoveringWalk(false);
      }
    }, RECOVERY_LOCK_MS);
    try {
      // Every page: a walk over the whole warehouse holds more shelves than one page serves [SSD-7662].
      const open = walkToResume(await listEveryOpenRound((params) => adminClient.listStockTakes(params)));
      if (open.length === 0) {
        return;
      }
      const shelves: Record<string, string> = {};
      const resumed: Record<string, string> = {};
      const persisted: Record<string, string> = {};
      const saved: Record<string, string> = {};
      const targets: Record<string, string[]> = {};
      const appliedRead: Record<string, AppliedCount> = {};
      /** Rows a full count walk added to a shelf: the round keeps the skus it adopted as found [SSD-7662]. */
      const foundRead: Record<string, true> = {};
      const read = await Promise.all(
        open.map(async (round) => ({ round, detail: await adminClient.getStockTake(round.docId) })),
      );
      read.forEach(({ round, detail }) => {
        shelves[round.locationCode] = round.docId;
        const adoptedRaw = (detail.doc?.['meta'] as { foundSkus?: unknown } | undefined)?.foundSkus;
        const adopted = Array.isArray(adoptedRaw)
          ? adoptedRaw.filter((sku): sku is string => typeof sku === 'string')
          : [];
        adopted.forEach((sku) => {
          foundRead[`${sku}@${round.locationCode}`] = true;
        });
        // The frozen set is what the round opened with; a product found on the shelf since, and any line the round
        // counted, belong to the walk too, or a refresh drops a found row out of it [SSD-7662].
        targets[round.locationCode] = [
          ...new Set([
            ...(round.targetSkus ?? frozenTargetsOf(detail) ?? []),
            ...adopted,
            ...detail.lines.map((line) => line.sku),
          ]),
        ];
        detail.lines.forEach((line) => {
          if (line.countedQty !== null && line.countedQty !== undefined) {
            const key = `${line.sku}@${round.locationCode}`;
            const value = String(line.countedQty);
            resumed[key] = value;
            persisted[countStateKey(round.docId, key)] = value;
            saved[countStateKey(round.docId, key)] = value;
            if (line.applied) {
              appliedRead[key] = {
                countedQty: line.countedQty,
                delta: line.diff,
                movedFromUnassigned: 0,
                at: line.appliedAt ?? null,
                ...annotationsOfLine(line),
              };
            }
          }
        });
      });
      if (recoveryRequest !== recoveryRequestRef.current) {
        return;
      }
      setApplied(appliedRead);
      persistedRef.current = persisted;
      scheduledCountsRef.current = { ...persisted };
      savedCountsRef.current = saved;
      setCounted(resumed);
      // Round ids are not reused, but a round this tab holds open is never one it stopped writing to.
      Object.values(shelves).forEach((docId) => closedRoundsRef.current.delete(docId));
      setOpenRoundsByShelf(shelves);
      setAuditTargetsByShelf(targets);
      const groupId = open[0]?.auditGroupId ?? uuidv4();
      setAuditGroupId(groupId);
      if (isFullCount(groupId)) {
        // A full count walk picks up on the shelf counted last, so the floor carries on where it stood [SSD-7662].
        const shelf = resumeShelf(walkShelves(Object.keys(shelves)), appliedRead);
        const started = open
          .map((round) => round.startedAt)
          .filter((at): at is string => Boolean(at))
          .sort()[0];
        setWalkFound(foundRead);
        setWalkStartedAt(started ?? null);
        if (shelf) {
          setCountingShelf(shelf);
          setQuery(shelf);
        }
      }
      setAuditOn(true);
    } catch {
      // A pool that cannot be read is not a walk that is not standing; the desk can start one by hand.
    } finally {
      window.clearTimeout(giveUp);
      if (recoveryRequest === recoveryRequestRef.current) {
        setRecoveringWalk(false);
      }
    }
  };
  useEffect(() => {
    void recoverWalk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Which shelves a walk holds is not settled: one is opening or closing, or a standing one is still being read. */
  const walkLocked = auditBusy || recoveringWalk;

  // While a walk is open the stock is read again every 20 seconds, so each row is counted against a recent book and a
  // row that moved after it was counted says so [SSD-7447]. A hidden tab, a read still out or a closing walk skips a beat.
  const refreshAuditRef = useRef<() => void>(() => undefined);
  refreshAuditRef.current = () => {
    if (document.visibilityState !== 'visible' || loadsOutRef.current > 0 || walkLocked || finishingAuditRef.current) {
      return;
    }
    void load(showZeroQty, undefined, true);
  };
  useEffect(() => {
    if (!auditOn) {
      return undefined;
    }
    const timer = window.setInterval(() => refreshAuditRef.current(), AUDIT_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [auditOn]);
  const pickabilityUnreported = rows.some((row) => row.pickable === undefined);
  // After a ✎ save the table holds its order, so the row that changed stays under the operator's eye; choosing a sort,
  // a filter, the zero-row toggle, the audit or Refresh lets the rows fall into order again, and so does the saved row
  // leaving the book [SSD-7449].
  const orderView = [sortMode, query, line, route, showZeroQty, auditOn, sortEpoch].join('\u0000');
  const holdingOrder =
    heldOrder !== null && heldOrder.view === orderView && rows.some((row) => keyOf(row) === heldOrder.key);
  // Nothing sweeps emptied rows away any more, so they pile up; a negative is a defect and always stays. A row just
  // counted down to 0 stays too while the order is held, or the new count would vanish before it is read.
  const withStock = showZeroQty
    ? rows
    : rows.filter((row) => row.onHand !== 0 || (holdingOrder && keyOf(row) === heldOrder?.key));
  const hiddenZeroQty = rows.length - withStock.length;
  const shortSkus = oversoldSkus(rows);
  // A scanned shelf label lists that shelf alone; any other text searches as before, plus shelf codes it starts [SSD-7529].
  const searchShelf = shelfNamedBy(query, rows);
  const sortedRows = sortRows(
    withStock.filter((row) => matchesSearch(row, query, searchShelf) && inLine(row, line) && inRoute(row, route)),
    auditOn ? 'location-asc' : sortMode,
  );
  const visible = holdingOrder && heldOrder ? keepOrder(sortedRows, heldOrder.keys) : sortedRows;
  // The search finds a row on screen; it does not decide what an audit covers. Start takes every row that Line and
  // Sourcing route keep, so clearing the search mid-walk brings the whole walk back on screen [SSD-7472].
  const auditScope = sortRows(
    withStock.filter((row) => inLine(row, line) && inRoute(row, route)),
    'location-asc',
  );
  // A book that does not add up sits the walk out (Needs fixing first), so the count said before Start leaves it out too.
  const countableScope = auditScope.filter((row) => !isOversold(row));
  // A save holds the order the operator was looking at, the last committed one. It runs before that order is recorded
  // again, and as a layout effect, so the re-sorted rows never reach the screen.
  useLayoutEffect(() => {
    if (savedKey && !auditOn) {
      setHeldOrder({ view: orderView, keys: committedOrderRef.current, key: savedKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  useLayoutEffect(() => {
    committedOrderRef.current = visible.map(keyOf);
  });
  // Let go for good once the view changes or the saved row is gone, so sorting back to the same view does not revive it.
  useLayoutEffect(() => {
    if (heldOrder && !holdingOrder) {
      setHeldOrder(null);
    }
  }, [heldOrder, holdingOrder]);
  // Memoized on the session alone, so a re-render (a toast, a keystroke) does not look like a new walk to auto-save.
  const sessionRows = useMemo(
    () =>
      sortRows(
        rows.filter((row) => {
          const targets = auditTargetsByShelf[row.locationCode];
          return Boolean(openRoundsByShelf[row.locationCode] && targets?.includes(row.sku));
        }),
        'location-asc',
      ),
    [auditTargetsByShelf, openRoundsByShelf, rows],
  );
  const auditRows = auditOn ? sessionRows : visible;
  // The walk's scope was fixed when it opened, but the search, Line and Route still pick which of its rows are on
  // screen, so the desk can find one sku among dozens of shelves. Counts, totals and Confirm keep the whole walk [SSD-7472].
  const shownRows = auditOn
    ? sessionRows.filter((row) => matchesSearch(row, query, searchShelf) && inLine(row, line) && inRoute(row, route))
    : visible;
  // A book that does not add up is left out of the walk rather than stopping it, so it is listed instead [SSD-6932].
  const needsFixing = sortRows(
    rows.filter((row) => isOversold(row) && !(auditOn && sessionRows.includes(row))),
    'location-asc',
  );
  // The shelf last scanned during a walk is where a product scanned next was found [SSD-7447].
  useEffect(() => {
    if (auditOn && searchShelf) {
      setCountingShelf(searchShelf);
    }
  }, [auditOn, searchShelf]);
  // A row just added (a found product) gets the cursor in its count box once it is drawn.
  useLayoutEffect(() => {
    const wanted = focusRowRef.current;
    if (!wanted) {
      return;
    }
    const index = shownRows.findIndex((row) => keyOf(row) === wanted);
    const input = index >= 0 ? countInputsRef.current[index] : null;
    if (input) {
      focusRowRef.current = null;
      input.focus();
    }
  });
  // A shelf the search lists alone can be counted on its own, or added to the walk that does not cover it [SSD-7447].
  const shelfOffer = searchShelf !== null && !openRoundsByShelf[searchShelf] ? searchShelf : null;
  // A product scanned after a shelf label, with no row of the walk on that shelf, is offered as found there.
  const fullCount = auditOn && isFullCount(auditGroupId);
  // A full count walk answers a scanned product itself, against the shelf it stands at, so the offer is not shown there.
  const foundOffers =
    auditOn && !fullCount && countingShelf !== null && searchShelf === null
      ? skusNamedBy(query, rows)
          .filter((sku) => !sessionRows.some((row) => row.sku === sku && row.locationCode === countingShelf))
          .slice(0, 3)
      : [];

  const hasActiveFilters = rows.length > 0 && (query.trim() !== '' || line !== 'all' || route !== 'all');
  const clearFilters = () => {
    setQuery('');
    setLine('all');
    setRoute('all');
    setShowZeroQty(false);
  };

  // A live audit writes each row the moment it is saved, so what it did is what was applied, not what is typed [SSD-7447].
  const tally = auditTally(
    sessionRows.flatMap((row) => {
      const entry = applied[keyOf(row)];
      return entry ? [{ diff: entry.delta, isNewAddition: false }] : [];
    }),
  );
  const countedRows = sessionRows.filter((row) => applied[keyOf(row)] !== undefined).length;
  /** Per shelf of the walk, the rows nobody has counted yet, in walk order. */
  const shelvesLeft: ShelfLeft[] = [];
  sessionRows.forEach((row) => {
    let shelf = shelvesLeft.find((entry) => entry.locationCode === row.locationCode);
    if (!shelf) {
      shelf = { locationCode: row.locationCode, left: 0, total: 0 };
      shelvesLeft.push(shelf);
    }
    shelf.total += 1;
    if (applied[keyOf(row)] === undefined) {
      shelf.left += 1;
    }
  });
  const rowsLeft = shelvesLeft.reduce((sum, shelf) => sum + shelf.left, 0);
  /** The walk's tally for the one shelf the search lists, when it lists one. */
  const shownShelf = searchShelf ? shelvesLeft.find((shelf) => shelf.locationCode === searchShelf) : undefined;
  const hasInvalidCounts = auditRows.some((row) => countInputError(counted[keyOf(row)] ?? '') !== null);

  // The full count walk's place and numbers, read from the walk's own rounds and counts [SSD-7662].
  const walkShelfList = useMemo(() => walkShelves(Object.keys(openRoundsByShelf)), [openRoundsByShelf]);
  const walkFoundSet = useMemo(() => new Set(Object.keys(walkFound)), [walkFound]);
  const fullCountSummary = walkSummary(sessionRows, applied, walkFoundSet);
  const fullCountHistory = fullCount ? walkHistory(sessionRows, applied, walkFoundSet) : [];
  const currentShelfTally = countingShelf
    ? shelvesLeft.find((shelf) => shelf.locationCode === countingShelf)
    : undefined;
  const choiceRows =
    codeChoice === null
      ? null
      : codeChoice
          .map((key) => sessionRows.find((row) => keyOf(row) === key))
          .filter((row): row is InventoryScreenRow => row !== undefined);

  /** One ADJUST correction is one single-line round, so a refused shelf leaves the confirmed ones confirmed. */
  const recordCounts = async (
    entries: AdjustmentEntry[],
  ): Promise<{ confirmed: number; total: number; versionsAfter: Record<string, number | undefined> }> => {
    const byLocation = new Map<string, AdjustmentEntry[]>();
    entries.forEach((entry) => {
      const at = entry.row.locationCode;
      byLocation.set(at, [...(byLocation.get(at) ?? []), entry]);
    });
    let confirmed = 0;
    /** Each written row's balance version after the write, so the next ✎ on it is checked against that one [SSD-7447]. */
    const versionsAfter: Record<string, number | undefined> = {};
    for (const [locationCode, lines] of byLocation) {
      const key = shelfKeysRef.current.get(locationCode) ?? uuidv4();
      shelfKeysRef.current.set(locationCode, key);
      let openedDocId: string | null = null;
      try {
        // The origin and each line's reason ride to the ledger, so Stock History can tell this from an audit.
        const { docId } = await adminClient.openStockTake({
          locationCode,
          idempotencyKey: key,
          origin: 'MANUAL_ADJUST',
        });
        openedDocId = docId;
        let inStock = true;
        for (const entry of lines) {
          // Written at once, as an audit count is, so a count below the reservation carries its tick and the server
          // keeps who gave it [SSD-7437 3·4]. The key follows the round's, so a retry of this save replays it.
          const result = await adminClient.countStockTake(docId, {
            sku: entry.row.sku,
            countedQty: entry.countedQty,
            expectedSystemQty: entry.expectedSystemQty,
            ...(entry.expectedVersion !== undefined ? { expectedVersion: entry.expectedVersion } : {}),
            ...(entry.reason ? { reason: entry.reason } : {}),
            apply: true,
            idempotencyKey: `${key}:${entry.row.sku}`,
            ...(entry.acknowledgeShortfall ? { acknowledgeShortfall: true } : {}),
          });
          inStock = inStock && result.applied === true;
          versionsAfter[keyOf(entry.row)] = result.applied === true ? result.versionAfter : undefined;
        }
        try {
          await adminClient.confirmStockTake(docId);
        } catch (failure) {
          if (!inStock) {
            throw failure;
          }
          // Every line is already in the stock and only the seal failed, so the round is closed and the save stands.
          await adminClient.cancelStockTake(docId, ADJUST_REFUSED_REASON).catch(() => undefined);
        }
      } catch (failure) {
        // An open round locks its shelf, so a refused count or confirm closes the round this call opened.
        // A round that was never ours to open (a 409 naming another walk) is left alone.
        if (openedDocId) {
          try {
            await adminClient.cancelStockTake(openedDocId, ADJUST_REFUSED_REASON);
            shelfKeysRef.current.delete(locationCode);
          } catch {
            // The server's refusal is the error worth showing; the keyed retry reuses the round if it is still open.
          }
        }
        throw new PartialAuditFailure(confirmed, byLocation.size, failure);
      }
      confirmed += 1;
      shelfKeysRef.current.delete(locationCode);
    }
    return { confirmed, total: byLocation.size, versionsAfter };
  };

  const applyAdjustment = async (
    row: InventoryScreenRow,
    countedQty: number,
    reason: AdjustReason | null,
    expectedOnHand: number,
    acknowledgeShortfall: boolean,
    expectedVersion: number | undefined,
    fromFixList = false,
  ) => {
    if (adjustInFlightRef.current) {
      return;
    }
    // A shelf carries one open round, so a ✎ on a shelf the walk holds, or is still opening, would land in the walk's own round.
    if (walkLocked || openRoundsByShelf[row.locationCode]) {
      setAdjusting(null);
      setFixing(null);
      showError(
        `Shelf ${shelfLabel(row.locationCode)} is open in the stock audit — adjust it once the audit is confirmed or exited.`,
      );
      return;
    }
    adjustInFlightRef.current = true;
    setAdjustBusy(true);
    try {
      const diff = countedQty - row.onHand;
      const { versionsAfter } = await recordCounts([
        {
          row,
          countedQty,
          diff,
          expectedSystemQty: expectedOnHand,
          reason: adjustReasonFor(diff, reason),
          acknowledgeShortfall,
          expectedVersion,
        },
      ]);
      invalidateInventoryReads();
      setAdjusting(null);
      setFixing(null);
      // Applying gives in-place feedback, not a toast [G-2 CP-9]; only a failure shouts.
      setSavedKey(keyOf(row));
      setRows((previous) =>
        previous.map((entry) =>
          // The version the write left, or none when the server did not say: a stale one would refuse the next ✎.
          keyOf(entry) === keyOf(row) ? withVersion(withOnHand(entry, countedQty), versionsAfter[keyOf(row)]) : entry,
        ),
      );
      if (fromFixList && !isOversold(withOnHand(row, countedQty))) {
        // The one exception: a repaired row leaves the fix-first list and takes its in-place marker with it.
        showSuccess(`✓ ${row.sku} adds up again — the next stock audit counts it.`);
      }
    } catch (failure) {
      // recordCounts wraps the wire error, so the server's reason sits one level down.
      const cause = failure instanceof PartialAuditFailure ? failure.cause : failure;
      const moved = staleStockRefusalOf(cause)?.staleLines.find((stale) => stale.sku === row.sku);
      if (moved) {
        // Someone changed this shelf after the box opened: nothing was applied, so the row is redrawn from the
        // balance it holds now and the box clears, so the next number is typed against that balance [SSD-7209].
        const message = `${describeStaleStock([moved])} Enter the quantity again.`;
        setRows((previous) =>
          previous.map((entry) =>
            keyOf(entry) === keyOf(row) ? withVersion(withOnHand(entry, moved.current), moved.currentVersion) : entry,
          ),
        );
        setAdjustConflict({
          key: keyOf(row),
          message,
          current: moved.current,
          ...(moved.currentVersion !== undefined ? { currentVersion: moved.currentVersion } : {}),
        });
        showError(message);
        return;
      }
      const short = belowReservedRefusalOf(cause);
      if (short) {
        // The reservations grew while the box was open: nothing was written, and the box stays for the tick.
        showError(
          `${short.countedQty} is below the ${short.reserved} reserved on ${shelfLabel(row.locationCode)} — tick the orders that go short, then apply again.`,
        );
        return;
      }
      // The editor closes on a refusal: leaving the typed number up reads as a value the book now holds.
      setAdjusting(null);
      setFixing(null);
      const held = heldShelfOf(cause);
      if (held) {
        const own = held.origin === 'STOCK_AUDIT' && isOwnRound(held.operator);
        showError(describeHeldShelf(held, shelfLabel(row.locationCode), own));
        // The walk was opened in another tab, or this one had not read it yet: picking it up locks its shelves here.
        if (own && !auditOn) {
          void recoverWalk();
        }
        return;
      }
      showFailure(cause, 'The adjustment was refused.');
    } finally {
      adjustInFlightRef.current = false;
      setAdjustBusy(false);
    }
  };

  /** The counts a standing round already holds, keyed the way the screen keys its rows. */
  const countsOf = async (
    docId: string,
    locationCode: string,
    localTargets: string[],
  ): Promise<{ counts: Record<string, string>; targetSkus: string[]; applied: Record<string, AppliedCount> }> => {
    const counts: Record<string, string> = {};
    const appliedRead: Record<string, AppliedCount> = {};
    const detail = await adminClient.getStockTake(docId);
    detail.lines.forEach((line) => {
      if (line.countedQty !== null && line.countedQty !== undefined) {
        counts[`${line.sku}@${locationCode}`] = String(line.countedQty);
        if (line.applied) {
          appliedRead[`${line.sku}@${locationCode}`] = {
            countedQty: line.countedQty,
            delta: line.diff,
            movedFromUnassigned: 0,
            at: line.appliedAt ?? null,
            ...annotationsOfLine(line),
          };
        }
      }
    });
    // The set the round froze is the walk; without one, the lines only name what was counted so far.
    const frozen = frozenTargetsOf(detail);
    return {
      counts,
      targetSkus: frozen ?? [...new Set([...localTargets, ...detail.lines.map((line) => line.sku)])],
      applied: appliedRead,
    };
  };

  /**
   * Redraws the rows a refusal named from the balance their shelf holds now, clears what was typed for them and
   * forgets what the round holds for them, so the old number is never re-sent against the new book: the row is
   * counted again from scratch, and Confirm stays off until it is [SSD-7209, decision D6].
   */
  const markStaleRecounts = useCallback((docId: string, locationCode: string, lines: StaleStockLine[]) => {
    const marked = Object.fromEntries(lines.map((line) => [keyOf({ sku: line.sku, locationCode }), line]));
    Object.keys(marked).forEach((key) => {
      const stateKey = countStateKey(docId, key);
      delete liftedStaleRef.current[key];
      delete persistedRef.current[stateKey];
      delete scheduledCountsRef.current[stateKey];
      delete savedCountsRef.current[stateKey];
      delete countSaveFailuresRef.current[stateKey];
      delete prefilledRef.current[stateKey];
      // The next count freezes the book again, from the balance the refusal reported.
      delete frozenRef.current[stateKey];
      delete applyKeysRef.current[stateKey];
      delete shortfallAckRef.current[stateKey];
    });
    // The refusal carries the shelf's balance now; without its version the next save is checked on the quantity alone.
    setRows((previous) =>
      previous.map((entry) => {
        const moved = marked[keyOf(entry)];
        return moved ? withVersion(withOnHand(entry, moved.current), moved.currentVersion) : entry;
      }),
    );
    setCounted((entries) => ({ ...entries, ...Object.fromEntries(Object.keys(marked).map((key) => [key, ''])) }));
    setStaleRecounts((standing) => ({ ...standing, ...marked }));
  }, []);

  /** Set once the walk's close-out helpers exist below; a count write that lands on a closed round calls through it. */
  const closedRoundRefusedRef = useRef<(locationCode: string, refusal: ClosedStockTakeRefusal, docId: string) => void>(
    () => undefined,
  );

  /**
   * What a saved count did to the stock [SSD-7447]: the row now holds the counted quantity, the WH#00 row gives up what
   * was moved from it with the reservations that followed, and the walk records the gap written. An older server only
   * saved the count on the round, so nothing is marked written and Finish applies it.
   */
  const noteApplied = useCallback(
    (
      row: InventoryScreenRow,
      value: string,
      result: CountStockTakeResult,
      barcodeCheck?: StockTakeBarcodeCheckInput,
    ) => {
      if (result.applied !== true) {
        return;
      }
      const key = keyOf(row);
      const countedQty = Number(value);
      const onHandAfter = result.onHandAfter ?? countedQty;
      const moved = result.movedFromUnassigned ?? 0;
      const reservationsMoved = result.reservationsMoved ?? 0;
      // A read already out predates this write and must not land over it.
      loadRequestRef.current += 1;
      setLoading(false);
      const patch = (entry: InventoryScreenRow): InventoryScreenRow => {
        if (keyOf(entry) === key) {
          return withVersion(
            withOnHand({ ...entry, reserved: entry.reserved + reservationsMoved }, onHandAfter),
            result.versionAfter,
          );
        }
        if (moved > 0 && entry.sku === row.sku && isUnassignedShelf(entry.locationCode)) {
          // The response carries no WH#00 version, so until the next read a WH#00 count is checked on its quantity alone.
          return withVersion(
            withOnHand({ ...entry, reserved: Math.max(0, entry.reserved - reservationsMoved) }, entry.onHand - moved),
            undefined,
          );
        }
        return entry;
      };
      foundRowsRef.current = foundRowsRef.current.map(patch);
      setRows((previous) => previous.map(patch));
      const recounted = recountedRef.current.delete(key);
      setApplied((standing) => {
        const before = recounted ? undefined : standing[key];
        // The line keeps its marks through a recount: the server stores them on the line, not on one count [SSD-7662].
        const marks = standing[key];
        return {
          ...standing,
          [key]: {
            countedQty,
            delta: (before?.delta ?? 0) + (result.delta ?? 0),
            movedFromUnassigned: (before?.movedFromUnassigned ?? 0) + moved,
            at: new Date().toISOString(),
            ...(result.versionAfter !== undefined ? { versionAfter: result.versionAfter } : {}),
            ...(marks?.barcodeCheck && { barcodeCheck: marks.barcodeCheck }),
            ...(barcodeCheck && { barcodeCheck }),
            ...(marks?.note && { note: marks.note }),
            ...(marks?.barcodeCorrection && { barcodeCorrection: marks.barcodeCorrection }),
            ...(marks?.movedFrom && { movedFrom: marks.movedFrom }),
          },
        };
      });
      setShortfallPrompts((standing) => {
        if (!standing[key]) {
          return standing;
        }
        const rest = { ...standing };
        delete rest[key];
        return rest;
      });
      setShortfallReasons((standing) => {
        if (!standing[key]) {
          return standing;
        }
        const rest = { ...standing };
        delete rest[key];
        return rest;
      });
      setReasonPrompts((standing) => {
        if (!standing[key]) {
          return standing;
        }
        const rest = { ...standing };
        delete rest[key];
        return rest;
      });
    },
    [],
  );

  /**
   * Writes one counted line to the stock through its round, and remembers it so the next save leaves it alone [SSD-7447].
   * The count is made against the book frozen when counting the row began, so a shelf that moved since refuses this row
   * alone rather than adjusting by a stale figure. Each value carries its own retry key: sent twice, it is written once.
   */
  const persistCount = useCallback(
    async (docId: string, row: InventoryScreenRow, text: string) => {
      const key = keyOf(row);
      const { sku } = row;
      const value = text.trim();
      if (!isValidCount(value)) {
        return Promise.resolve();
      }
      const stateKey = countStateKey(docId, key);
      const queued = countWriteQueuesRef.current[stateKey];
      if (persistedRef.current[stateKey] === value && scheduledCountsRef.current[stateKey] === value) {
        return queued ?? Promise.resolve();
      }
      if (scheduledCountsRef.current[stateKey] === value && queued) {
        return queued;
      }
      scheduledCountsRef.current[stateKey] = value;
      // Counting began without a keystroke (a box restored after a refresh): the book on screen now is the one.
      frozenRef.current[stateKey] ??= bookOf(row);
      const write = (queued ?? Promise.resolve())
        .catch(() => undefined)
        .then(async () => {
          if (persistedRef.current[stateKey] === value) {
            return;
          }
          if (closedRoundsRef.current.has(docId)) {
            // A colleague closed this round while the write waited: it is no longer ours to send to.
            delete scheduledCountsRef.current[stateKey];
            return;
          }
          const frozen =
            frozenRef.current[stateKey] ?? bookOf(rowsRef.current.find((entry) => keyOf(entry) === key) ?? row);
          // The tick's reason is the later choice, so it wins over the one picked for the difference.
          const reason = shortfallAckRef.current[stateKey] ?? countReasonRef.current[stateKey];
          if (Number(value) !== frozen.onHand && !reason) {
            // A count that differs from the book is not written without a reason: the row waits for one [SSD-7591].
            delete scheduledCountsRef.current[stateKey];
            setReasonPrompts((waiting) => ({ ...waiting, [key]: { countedQty: Number(value), book: frozen.onHand } }));
            return;
          }
          const standing = applyKeysRef.current[stateKey];
          const idempotencyKey = standing?.value === value ? standing.key : uuidv4();
          applyKeysRef.current[stateKey] = { value, key: idempotencyKey };
          // Units the book still holds in WH#00 come from there before any are added, so stock already on the book is
          // not counted twice [SSD-7447 decision 4]; the counter can untick it for a row.
          const unassigned = rowsRef.current.find(
            (entry) => entry.sku === sku && isUnassignedShelf(entry.locationCode),
          );
          const fromUnassigned =
            !isUnassignedShelf(row.locationCode) &&
            !keepUnassignedRef.current[key] &&
            (unassigned?.onHand ?? 0) > 0 &&
            Number(value) > frozen.onHand;
          // The check this save carries; a scan landing while it is out stays for the next save [SSD-7662].
          const sentCheck = scanCheckRef.current[key];
          try {
            const result = await adminClient.countStockTake(docId, {
              sku,
              countedQty: Number(value),
              expectedSystemQty: frozen.onHand,
              ...(frozen.version !== undefined ? { expectedVersion: frozen.version } : {}),
              apply: true,
              idempotencyKey,
              ...(fromUnassigned ? { fromUnassigned: true } : {}),
              ...(reason ? { reason } : {}),
              ...(shortfallAckRef.current[stateKey] ? { acknowledgeShortfall: true } : {}),
              ...(foundRef.current[stateKey] ? { found: true } : {}),
              ...(sentCheck ? { annotations: { barcodeCheck: sentCheck } } : {}),
            });
            if (sentCheck && scanCheckRef.current[key] === sentCheck) {
              delete scanCheckRef.current[key];
            }
            persistedRef.current[stateKey] = value;
            savedCountsRef.current[stateKey] = value;
            // The recount is saved, applied at once or held for Finish on an older server: an emptied box no longer
            // brings the stale note back, and one put back meanwhile goes.
            delete liftedStaleRef.current[key];
            setStaleRecounts((standing) => {
              if (!standing[key]) {
                return standing;
              }
              const rest = { ...standing };
              delete rest[key];
              return rest;
            });
            if (scheduledCountsRef.current[stateKey] === value) {
              delete countSaveFailuresRef.current[stateKey];
            }
            delete applyKeysRef.current[stateKey];
            if (result.applied === true) {
              if (scheduledCountsRef.current[stateKey] === value) {
                // The row is written: an amendment starts from the balance it holds when the counter types again.
                delete frozenRef.current[stateKey];
              } else {
                // An amendment typed while this save was out is counted against what this save left on the shelf.
                frozenRef.current[stateKey] =
                  result.versionAfter !== undefined
                    ? { onHand: result.onHandAfter ?? Number(value), version: result.versionAfter }
                    : { onHand: result.onHandAfter ?? Number(value) };
              }
            }
            delete shortfallAckRef.current[stateKey];
            delete countReasonRef.current[stateKey];
            delete prefilledRef.current[stateKey];
            // A replayed save answers as the first one did, so a check sent with it may not be on the line; a scan that
            // landed while the save was out was not sent at all. Either is written on its own once the line is counted.
            const confirmed = result.replayed ? undefined : sentCheck;
            noteApplied(row, value, result, confirmed);
            const pendingCheck =
              scanCheckRef.current[key] && scanCheckRef.current[key] !== sentCheck
                ? scanCheckRef.current[key]
                : result.replayed
                  ? sentCheck
                  : undefined;
            if (pendingCheck && result.applied === true) {
              delete scanCheckRef.current[key];
              adminClient
                .annotateStockTakeLine(docId, { sku, barcodeCheck: pendingCheck })
                .then(() =>
                  setApplied((standing) =>
                    standing[key] ? { ...standing, [key]: { ...standing[key], barcodeCheck: pendingCheck } } : standing,
                  ),
                )
                .catch(() => showError(`${sku}: the barcode check did not save. Scan it again.`));
            }
          } catch (failure) {
            const moved = staleStockRefusalOf(failure)?.staleLines.filter((line) => line.sku === sku) ?? [];
            const closed = closedStockTakeRefusalOf(failure);
            const short = belowReservedRefusalOf(failure);
            if (short) {
              // Nothing was written: the row waits for the tick, and the same value then goes again under the same key.
              delete countSaveFailuresRef.current[stateKey];
              setShortfallPrompts((standing) => ({ ...standing, [key]: short }));
              // The reason already picked for the difference stands for the short count too: only the tick is left.
              const known = countReasonRef.current[stateKey];
              if (known) {
                setShortfallReasons((standing) => (standing[key] ? standing : { ...standing, [key]: known }));
              }
              throw failure;
            }
            if (scheduledCountsRef.current[stateKey] === value) {
              countSaveFailuresRef.current[stateKey] = failure;
            }
            if (moved.length > 0) {
              // The shelf moved since counting it began, so nothing was written: the row is cleared for a new count.
              markStaleRecounts(docId, row.locationCode, moved);
              showError(`${describeStaleStock(moved)} Nothing was saved for it — count it again.`);
            } else if (closed) {
              delete applyKeysRef.current[stateKey];
              // The round is gone, not a save to retry: Finish must not report it as a count that failed.
              delete countSaveFailuresRef.current[stateKey];
              // A colleague sealed or abandoned this shelf's round: it leaves the walk and the shelf is read again.
              closedRoundRefusedRef.current(row.locationCode, closed, docId);
            } else {
              // A request the server may have written keeps its key, so sending it again replays rather than writes twice.
              if (axios.isAxiosError(failure) && failure.response && failure.response.status < 500) {
                delete applyKeysRef.current[stateKey];
              }
              showFailure(failure, 'The count could not be saved.');
            }
            throw failure;
          }
        });
      countWriteQueuesRef.current[stateKey] = write;
      void write.then(
        () => {
          if (countWriteQueuesRef.current[stateKey] === write) {
            delete countWriteQueuesRef.current[stateKey];
          }
        },
        () => {
          if (countWriteQueuesRef.current[stateKey] === write) {
            delete countWriteQueuesRef.current[stateKey];
          }
        },
      );
      return write;
    },
    [adminClient, markStaleRecounts, noteApplied, showError, showFailure],
  );

  /**
   * Every count the floor typed and has not saved yet goes before the walk closes. A row waiting for its short-count
   * tick, its reason or a recount is left out: it is not in the stock, so it is listed with the rows nobody counted.
   */
  const flushCounts = async (): Promise<void> => {
    if (auditRows.some((row) => countInputError(countedRef.current[keyOf(row)] ?? '') !== null)) {
      throw new Error(COUNT_ERROR);
    }
    const activeDocIds = new Set(
      Object.values(openRoundsByShelf).filter((docId) => !closedRoundsRef.current.has(docId)),
    );
    await Promise.all(
      Object.entries(countWriteQueuesRef.current)
        .filter(([key]) => activeDocIds.has(key.split('\u0000', 1)[0]!))
        .map(([, write]) => write.catch(() => undefined)),
    );
    await Promise.all(
      auditRows.map((row) => {
        const docId = openRoundsByShelf[row.locationCode];
        if (
          !docId ||
          closedRoundsRef.current.has(docId) ||
          shortfallPrompts[keyOf(row)] ||
          reasonPrompts[keyOf(row)] ||
          staleRecounts[keyOf(row)]
        ) {
          return Promise.resolve();
        }
        return persistCount(docId, row, countedRef.current[keyOf(row)] ?? '').catch(() => undefined);
      }),
    );
    if (
      Object.keys(countSaveFailuresRef.current).some((key) => {
        const docId = key.split('\u0000', 1)[0]!;
        return activeDocIds.has(docId) && !closedRoundsRef.current.has(docId);
      })
    ) {
      throw new Error('The count could not be saved.');
    }
  };

  /** The box the floor saves a count from: Enter, or leaving it, writes the count; a box never typed in is left alone. */
  const saveCount = (row: InventoryScreenRow, confirmMatch: boolean) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId) {
      return;
    }
    const key = keyOf(row);
    const stateKey = countStateKey(docId, key);
    if (prefilledRef.current[stateKey] !== undefined) {
      if (!confirmMatch) {
        return;
      }
      // Enter on the book value says the shelf matches: that is a count, sent like any other [SSD-7447].
      delete prefilledRef.current[stateKey];
      delete persistedRef.current[stateKey];
      delete scheduledCountsRef.current[stateKey];
      frozenRef.current[stateKey] ??= bookOf(row);
    }
    const done = applied[key];
    const typed = (countedRef.current[key] ?? '').trim();
    const movedSince =
      done !== undefined &&
      (row.onHand !== done.countedQty ||
        (done.versionAfter !== undefined && row.version !== undefined && row.version !== done.versionAfter));
    if (
      confirmMatch &&
      movedSince &&
      typed === persistedRef.current[stateKey] &&
      !countWriteQueuesRef.current[stateKey]
    ) {
      // The shelf moved after this row was saved and the counter confirms the same number again: that is a new count,
      // made against the book as it stands now, so the stock goes back to what the shelf holds [SSD-7447].
      delete persistedRef.current[stateKey];
      delete scheduledCountsRef.current[stateKey];
      frozenRef.current[stateKey] = bookOf(row);
    }
    if (shortfallPrompts[key] && !shortfallAckRef.current[stateKey]) {
      return;
    }
    void persistCount(docId, row, countedRef.current[key] ?? '').catch(() => undefined);
  };

  /**
   * Counts typed but not yet saved that differ from the book and have no reason: each row asks for one, and Finish waits
   * for them rather than closing the shelf on a count the stock never got [SSD-7591]. Returns how many rows wait.
   */
  const holdCountsWithoutReason = (): number => {
    const waiting: Record<string, { countedQty: number; book: number }> = {};
    auditRows.forEach((row) => {
      const docId = openRoundsByShelf[row.locationCode];
      const key = keyOf(row);
      const typed = (countedRef.current[key] ?? '').trim();
      if (!docId || !isValidCount(typed) || shortfallPrompts[key] || staleRecounts[key]) {
        return;
      }
      const stateKey = countStateKey(docId, key);
      if (persistedRef.current[stateKey] === typed || prefilledRef.current[stateKey] !== undefined) {
        return;
      }
      const book = (frozenRef.current[stateKey] ?? bookOf(row)).onHand;
      if (Number(typed) !== book && !countReasonRef.current[stateKey] && !shortfallAckRef.current[stateKey]) {
        waiting[key] = { countedQty: Number(typed), book };
      }
    });
    if (Object.keys(waiting).length > 0) {
      setReasonPrompts((standing) => ({ ...standing, ...waiting }));
    }
    const live = new Set(auditRows.map(keyOf));
    return new Set([...Object.keys(waiting), ...Object.keys(reasonPrompts).filter((key) => live.has(key))]).size;
  };

  /** Why a count differs from the book: once it is picked, the count waiting for it is written with it [SSD-7591]. */
  const giveCountReason = (row: InventoryScreenRow, picked: AdjustReason, handBack = true) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId) {
      return;
    }
    const key = keyOf(row);
    countReasonRef.current[countStateKey(docId, key)] = picked;
    setReasonPrompts((standing) => {
      const rest = { ...standing };
      delete rest[key];
      return rest;
    });
    void persistCount(docId, row, countedRef.current[key] ?? '').catch(() => undefined);
    if (fullCount && handBack) {
      // The reason was the last word on the row: the next thing read is the next product, so the scan box takes the
      // cursor back from the reason box [SSD-7662].
      searchInput.settle();
    }
  };

  /** A reason typed into its box, written once the counter presses Enter (cursor back to the scan box) or leaves it. */
  const commitTypedReason = (row: InventoryScreenRow, handBack: boolean) => {
    const key = keyOf(row);
    const picked = typedReasonsRef.current[key];
    if (!picked) {
      return;
    }
    delete typedReasonsRef.current[key];
    setTypedReasons((standing) => {
      const rest = { ...standing };
      delete rest[key];
      return rest;
    });
    giveCountReason(row, picked, handBack);
  };

  /** The tick on a count below the reservation, given once a reason is picked: the same count goes again with both [SSD-7437 3·4]. */
  const acknowledgeShortfall = (row: InventoryScreenRow) => {
    const docId = openRoundsByShelf[row.locationCode];
    const reason = shortfallReasons[keyOf(row)];
    if (!docId || !reason) {
      return;
    }
    shortfallAckRef.current[countStateKey(docId, keyOf(row))] = reason;
    void persistCount(docId, row, countedRef.current[keyOf(row)] ?? '').catch(() => undefined);
    if (fullCount) {
      searchInput.settle();
    }
  };

  // An untouched line stands for "matches the book", so a reloaded book moves it too; nothing was sent for it [D-n].
  useEffect(() => {
    if (!auditOn) {
      return;
    }
    const moved: Record<string, string> = {};
    for (const row of sessionRows) {
      const docId = openRoundsByShelf[row.locationCode];
      if (!docId) {
        continue;
      }
      const stateKey = countStateKey(docId, keyOf(row));
      const prefilled = prefilledRef.current[stateKey];
      const book = String(row.onHand);
      if (
        prefilled === undefined ||
        prefilled === book ||
        savedCountsRef.current[stateKey] !== undefined ||
        countedRef.current[keyOf(row)] !== prefilled
      ) {
        continue;
      }
      prefilledRef.current[stateKey] = book;
      persistedRef.current[stateKey] = book;
      scheduledCountsRef.current[stateKey] = book;
      moved[keyOf(row)] = book;
    }
    if (Object.keys(moved).length > 0) {
      setCounted((entries) => ({ ...entries, ...moved }));
    }
  }, [auditOn, openRoundsByShelf, sessionRows]);

  /**
   * One round per shelf, freezing its skus: widening the filter mid-walk must not grow what was opened [D-n]. A full count
   * walk opens the same shelves under its own mark and leaves every box blank, so nothing reads as counted until it is
   * [SSD-7662].
   */
  const startAudit = async (mode: 'audit' | 'full-count' = 'audit') => {
    // Nothing on screen reads as "nothing matches", so the desk clears the search before it starts rather than after.
    if (visible.length === 0) {
      return;
    }
    // Counting a row whose book does not add up would seal a figure already known to be wrong, so it sits this walk out.
    const countable = countableScope;
    if (countable.length === 0) {
      showWarning(
        `There is nothing to count — ${stockIntegritySummary(auditScope, true)}. Repair them under "Needs fixing first", then start the audit.`,
        undefined,
        AUTO_DISMISS,
      );
      return;
    }
    // A move still out would land on a shelf this walk is about to hold [SSD-7456].
    if (relocatingRef.current.size > 0) {
      return;
    }
    // A ✎ box left open would apply on a shelf this walk is about to hold.
    setAdjusting(null);
    setFixing(null);
    setEditingShelf(null);
    recoveryRequestRef.current += 1;
    // A read this start overtakes would never settle the flag itself.
    setRecoveringWalk(false);
    const shelves = new Map<string, string[]>();
    // Rounds open in walk order, so the shelf a refusal names is the one the floor was about to stand at.
    sortRows(countable, 'location-asc').forEach((row) =>
      shelves.set(row.locationCode, [...(shelves.get(row.locationCode) ?? []), row.sku]),
    );
    const opened: Record<string, string> = {};
    const targets: Record<string, string[]> = {};
    /** Shelves whose round was already standing, so their counts come from the server rather than the book. */
    const joined: string[] = [];
    const fullCountWalk = mode === 'full-count';
    const groupId = fullCountWalk ? fullCountGroupId(uuidv4()) : auditGroupId;
    setAuditBusy(true);
    try {
      for (const [locationCode, targetSkus] of shelves) {
        const idempotencyKey = shelfKeysRef.current.get(locationCode) ?? uuidv4();
        shelfKeysRef.current.set(locationCode, idempotencyKey);
        try {
          const { docId } = await adminClient.openStockTake({
            locationCode,
            idempotencyKey,
            auditGroupId: groupId,
            targetSkus,
          });
          opened[locationCode] = docId;
          targets[locationCode] = targetSkus;
        } catch (failure) {
          // The shelf already has our own round open; joining it is the point of a server session [D-n].
          const standing = standingRoundOf(failure);
          if (standing && standing.auditGroupId === groupId && isOwnRound(standing.operator)) {
            opened[locationCode] = standing.docId;
            targets[locationCode] = targetSkus;
            joined.push(locationCode);
            continue;
          }
          // Half a walk is worse than none: the rounds this walk opened are closed again before reporting.
          // A joined round belongs to the walk that opened it, so it stays open with its counts as found.
          for (const [shelf, docId] of Object.entries(opened)) {
            if (!joined.includes(shelf)) {
              try {
                await adminClient.cancelStockTake(docId, ABANDON_REASON);
                shelfKeysRef.current.delete(shelf);
              } catch {
                // A failed rollback leaves the round open and its key available for retry.
              }
            }
          }
          if (standing) {
            // Someone else is counting this shelf; their round is theirs to finish, never ours to merge in.
            showError(
              `Shelf ${locationCode} is being counted by ${standing.operator ?? 'another worker'} — the audit was not started.`,
            );
          } else {
            const held = heldShelfOf(failure);
            if (held) {
              const own = held.origin === 'STOCK_AUDIT' && isOwnRound(held.operator);
              showError(`${describeHeldShelf(held, shelfLabel(locationCode), own)} The audit was not started.`);
            } else {
              showFailure(failure, `Shelf ${locationCode} would not open — the audit was not started.`);
            }
          }
          return;
        }
      }
      const prefill: Record<string, string> = {};
      const saved: Record<string, string> = {};
      const appliedJoined: Record<string, AppliedCount> = {};
      // A round we joined rather than opened already holds counts; those outrank the book value.
      for (const locationCode of joined) {
        let contents: Awaited<ReturnType<typeof countsOf>>;
        try {
          contents = await countsOf(opened[locationCode] ?? '', locationCode, targets[locationCode] ?? []);
        } catch (failure) {
          for (const [shelf, docId] of Object.entries(opened)) {
            if (!joined.includes(shelf)) {
              try {
                await adminClient.cancelStockTake(docId, ABANDON_REASON);
                shelfKeysRef.current.delete(shelf);
              } catch {
                // A failed rollback leaves the round open and its key available for retry.
              }
            }
          }
          showFailure(failure, `Shelf ${locationCode} could not be read — the audit was not started.`);
          return;
        }
        targets[locationCode] = contents.targetSkus;
        Object.assign(prefill, contents.counts);
        Object.assign(appliedJoined, contents.applied);
        Object.entries(contents.counts).forEach(([key, value]) => {
          saved[countStateKey(opened[locationCode] ?? '', key)] = value;
        });
      }
      const rowsInWalk = rows.filter((row) => targets[row.locationCode]?.includes(row.sku));
      rowsInWalk.forEach((row) => {
        // A full count box starts blank: blank is not counted, so a shelf is never confirmed by Enter alone [SSD-7662].
        if (prefill[keyOf(row)] === undefined && !fullCountWalk) {
          prefill[keyOf(row)] = String(row.onHand);
        }
      });
      // The book value counts as already told to the server: a matching shelf is one keystroke, not one request.
      const persisted = Object.fromEntries(
        rowsInWalk.flatMap((row) => {
          const docId = opened[row.locationCode];
          const value = prefill[keyOf(row)];
          return docId && value !== undefined ? [[countStateKey(docId, keyOf(row)), value]] : [];
        }),
      );
      persistedRef.current = persisted;
      scheduledCountsRef.current = { ...persisted };
      savedCountsRef.current = saved;
      prefilledRef.current = Object.fromEntries(
        Object.entries(persisted).filter(([stateKey]) => saved[stateKey] === undefined),
      );
      setCounted(prefill);
      setApplied(appliedJoined);
      Object.values(opened).forEach((docId) => closedRoundsRef.current.delete(docId));
      setOpenRoundsByShelf(opened);
      setAuditTargetsByShelf(targets);
      setAuditGroupId(groupId);
      if (fullCountWalk) {
        const shelvesOpened = walkShelves(Object.keys(opened));
        const first = shelvesOpened[0] ?? null;
        setWalkFound({});
        setWalkStartedAt(new Date().toISOString());
        setCountingShelf(first);
        setQuery(first ?? '');
        setWalkStatus({
          text: `Full count walk opened on ${shelvesOpened.length} ${
            shelvesOpened.length === 1 ? 'shelf' : 'shelves'
          }. Scan the products on ${first ? shelfLabel(first) : 'the first shelf'}.`,
          kind: 'ok',
        });
        // The cursor waits in the scan box for the first product, once the shelf is listed.
        window.setTimeout(() => searchInput.settle(), 0);
      }
      setAuditOn(true);
    } finally {
      setAuditBusy(false);
    }
  };

  /** Says why a shelf would not open into the walk: another worker's round, a round that holds it, or the server's reason. */
  const refuseShelf = (failure: unknown, locationCode: string, outcome: string) => {
    const standing = standingRoundOf(failure);
    if (standing) {
      // Someone else is counting this shelf; their round is theirs to finish, never ours to merge in.
      showError(
        `Shelf ${shelfLabel(locationCode)} is being counted by ${standing.operator ?? 'another worker'} — ${outcome}`,
      );
      return;
    }
    const held = heldShelfOf(failure);
    if (held) {
      const own = held.origin === 'STOCK_AUDIT' && isOwnRound(held.operator);
      showError(`${describeHeldShelf(held, shelfLabel(locationCode), own)} ${outcome}`);
      return;
    }
    showFailure(failure, `Shelf ${shelfLabel(locationCode)} would not open — ${outcome}`);
  };

  /**
   * Opens one shelf into the walk, its rows frozen as they stand, and answers its round; the shelf's own round is joined
   * when it is already standing in this walk [SSD-7447]. This is how a shelf is counted on its own, by scanning its
   * label, and how a shelf the walk did not cover is added to it.
   */
  const openShelfIntoWalk = async (
    locationCode: string,
    {
      groupId = auditGroupId,
      onFailure,
    }: {
      groupId?: string;
      /** Takes the failure instead of a toast, for a caller that says once what it skipped [SSD-7583]. */
      onFailure?: (failure: unknown) => void;
    } = {},
  ): Promise<string | null> => {
    const existing = openRoundsByShelf[locationCode];
    if (existing) {
      return existing;
    }
    const targetSkus = rowsRef.current
      .filter((row) => row.locationCode === locationCode && !isOversold(row))
      .map((row) => row.sku);
    const idempotencyKey = shelfKeysRef.current.get(locationCode) ?? uuidv4();
    shelfKeysRef.current.set(locationCode, idempotencyKey);
    let docId: string;
    let contents: Awaited<ReturnType<typeof countsOf>> | null = null;
    try {
      ({ docId } = await adminClient.openStockTake({
        locationCode,
        idempotencyKey,
        auditGroupId: groupId,
        targetSkus,
      }));
    } catch (failure) {
      const standing = standingRoundOf(failure);
      if (!standing || standing.auditGroupId !== groupId || !isOwnRound(standing.operator)) {
        if (onFailure) {
          onFailure(failure);
        } else {
          refuseShelf(failure, locationCode, 'it was not added to the audit.');
        }
        return null;
      }
      docId = standing.docId;
      try {
        contents = await countsOf(docId, locationCode, targetSkus);
      } catch (readFailure) {
        if (onFailure) {
          onFailure(readFailure);
        } else {
          showFailure(
            readFailure,
            `Shelf ${shelfLabel(locationCode)} could not be read — it was not added to the audit.`,
          );
        }
        return null;
      }
    }
    const targets = contents?.targetSkus ?? targetSkus;
    const prefill: Record<string, string> = {};
    rowsRef.current
      .filter((row) => row.locationCode === locationCode && targets.includes(row.sku))
      .forEach((row) => {
        const stateKey = countStateKey(docId, keyOf(row));
        const saved = contents?.counts[keyOf(row)];
        if (saved === undefined && isFullCount(groupId)) {
          // A full count box starts blank and nothing is told to the server until the floor types [SSD-7662].
          prefill[keyOf(row)] = '';
          return;
        }
        const value = saved ?? String(row.onHand);
        prefill[keyOf(row)] = value;
        persistedRef.current[stateKey] = value;
        scheduledCountsRef.current[stateKey] = value;
        if (saved !== undefined) {
          savedCountsRef.current[stateKey] = saved;
        } else {
          prefilledRef.current[stateKey] = value;
        }
      });
    setCounted((entries) => ({ ...entries, ...prefill }));
    setApplied((standing) => ({ ...standing, ...(contents?.applied ?? {}) }));
    closedRoundsRef.current.delete(docId);
    setOpenRoundsByShelf((current) => ({ ...current, [locationCode]: docId }));
    setAuditTargetsByShelf((current) => ({ ...current, [locationCode]: targets }));
    return docId;
  };

  /** Count this shelf / Add this shelf: the shelf the search lists alone is opened, starting a walk when none is open. */
  const countShelf = async (locationCode: string) => {
    if (walkLocked || relocatingRef.current.size > 0) {
      return;
    }
    if (!auditOn) {
      // A ✎ box left open would apply on a shelf this walk is about to hold.
      setAdjusting(null);
      setFixing(null);
      setEditingShelf(null);
      recoveryRequestRef.current += 1;
      setRecoveringWalk(false);
    }
    setAuditBusy(true);
    try {
      const docId = await openShelfIntoWalk(locationCode);
      if (docId && !auditOn) {
        setAuditOn(true);
      }
      if (docId) {
        setCountingShelf(locationCode);
      }
    } finally {
      setAuditBusy(false);
    }
  };

  /**
   * The shelves confirmed by an audit in the window, read newest first page by page until the pages pass it. A server
   * that answers without a cursor serves one page, which still holds the latest counts.
   */
  const recentlyCheckedShelves = async (cutoff: number): Promise<Map<string, number>> => {
    const read: StockTakeRound[] = [];
    let before: string | undefined;
    for (let page = 0; page < RANDOM_CHECK_HISTORY_PAGES; page += 1) {
      const { rounds: pageRounds, nextCursor } = await adminClient.listStockTakes({
        status: 'CONFIRMED',
        limit: RANDOM_CHECK_HISTORY_PAGE_SIZE,
        ...(before ? { before } : {}),
      });
      read.push(...pageRounds);
      if (!nextCursor || pagePassedWindow(pageRounds, cutoff)) {
        break;
      }
      before = nextCursor;
    }
    return lastCheckedByShelf(read, cutoff);
  };

  /**
   * Random check: opens the number of shelves in the box, drawn from what Line, Sourcing route and Show zero-quantity
   * rows keep, shelves no audit confirmed in the last two weeks first [SSD-7583]. During a Random check it adds that
   * many more. A shelf another worker holds is skipped and the next one drawn, since a sample has no shelf it must have.
   */
  const startRandomCheck = async () => {
    const count = randomCheckCount(randomCount);
    if (count === null) {
      showWarning(`Type how many shelves to draw, from 1 to ${RANDOM_CHECK_MAX}.`, undefined, AUTO_DISMISS);
      return;
    }
    if (walkLocked || relocatingRef.current.size > 0 || (auditOn && !isRandomCheck(auditGroupId))) {
      return;
    }
    if (!auditOn) {
      // A ✎ box left open would apply on a shelf this walk is about to hold.
      setAdjusting(null);
      setFixing(null);
      setEditingShelf(null);
      recoveryRequestRef.current += 1;
      setRecoveringWalk(false);
    }
    const groupId = auditOn ? auditGroupId : randomCheckGroupId(uuidv4());
    setAuditBusy(true);
    try {
      let lastChecked: Map<string, number>;
      const held = new Set(Object.keys(openRoundsByShelf));
      try {
        lastChecked = await recentlyCheckedShelves(recentCutoff(Date.now()));
        // A shelf another round holds would only be refused, so it is not drawn.
        // Every page of them, or a held shelf past the first page is drawn and only refused [SSD-7662].
        const standing = await listEveryOpenRound((params) => adminClient.listStockTakes(params));
        standing.forEach((round) => held.add(round.locationCode));
      } catch (failure) {
        showFailure(failure, 'Past audits could not be read, so no shelves were drawn.');
        return;
      }
      const order = randomShelfOrder(drawableShelves(countableScope, held), lastChecked);
      if (order.length === 0) {
        showWarning(
          'There is no shelf to draw: every shelf on screen is in a round already, or holds no countable row.',
          undefined,
          AUTO_DISMISS,
        );
        return;
      }
      // A walk stays within what a refresh reads back of it, so drawing more stops at the maximum [SSD-7583].
      const room = Math.min(count, RANDOM_CHECK_MAX - Object.keys(openRoundsByShelf).length);
      if (room <= 0) {
        showWarning(
          `This Random check already holds ${RANDOM_CHECK_MAX} shelves. Finish it before drawing more.`,
          undefined,
          AUTO_DISMISS,
        );
        return;
      }
      const opened: string[] = [];
      let skipped = 0;
      /** A failure that is not a shelf someone holds: the server may have opened the round without answering. */
      let stopped: { shelf: string; failure: unknown } | null = null;
      for (const shelf of order) {
        if (opened.length >= room) {
          break;
        }
        let refusal: unknown = null;
        const docId = await openShelfIntoWalk(shelf, {
          groupId,
          onFailure: (failure) => {
            refusal = failure;
          },
        });
        if (docId) {
          opened.push(shelf);
        } else if (standingRoundOf(refusal) || heldShelfOf(refusal)) {
          skipped += 1;
        } else {
          stopped = { shelf, failure: refusal };
          break;
        }
      }
      // Shelves already opened stay in the walk whatever happened next, so Finish and Exit can close them.
      if (opened.length > 0 && !auditOn) {
        setAuditGroupId(groupId);
        setAuditOn(true);
      }
      if (stopped) {
        showFailure(
          stopped.failure,
          `Random check stopped at shelf ${shelfLabel(stopped.shelf)} after ${opened.length} ${
            opened.length === 1 ? 'shelf' : 'shelves'
          }. Refresh to see whether it opened, then draw again.`,
        );
        return;
      }
      if (opened.length === 0) {
        showError('None of the drawn shelves would open: they are held by another round. Try again shortly.');
        return;
      }
      const recent = opened.filter((shelf) => lastChecked.has(shelf)).length;
      const notes = [
        opened.length < count ? `only ${opened.length} of ${count} could be drawn` : '',
        recent > 0
          ? `${recent} of them were counted in the last ${RECENTLY_CHECKED_DAYS} days, for want of others`
          : '',
        skipped > 0 ? `${skipped} held by another round were skipped` : '',
      ].filter(Boolean);
      const message = `Random check: ${opened.length} ${opened.length === 1 ? 'shelf' : 'shelves'} added (${opened
        .map(shelfLabel)
        .join(', ')})${notes.length > 0 ? ` — ${notes.join('; ')}` : ''}.`;
      if (notes.length > 0) {
        showWarning(message);
      } else {
        showSuccess(message);
      }
    } finally {
      setAuditBusy(false);
    }
  };

  /**
   * A product the counter found on a shelf the book has no row for joins the walk as a row at 0, to be counted; a row the
   * book has there but the walk left out is taken in as it stands. Either way its save says it was found [SSD-7447].
   */
  const addFoundRow = async (sku: string, locationCode: string, candidate?: ShelfCandidate) => {
    setAuditBusy(true);
    let docId: string | null;
    try {
      docId = await openShelfIntoWalk(locationCode);
    } finally {
      setAuditBusy(false);
    }
    if (!docId) {
      return;
    }
    const standing = rowsRef.current.find((row) => row.sku === sku && row.locationCode === locationCode);
    if (standing && isOversold(standing)) {
      // A book that does not add up sits the walk out; it is repaired under Needs fixing first, not counted in here.
      showError(
        `${sku} on ${shelfLabel(locationCode)} reserves more than it holds. Repair it under Needs fixing first, then count it.`,
      );
      return;
    }
    // A product the book holds nowhere comes from the catalog, at 0, for the full count walk [SSD-7662].
    const source =
      rowsRef.current.find((row) => row.sku === sku) ?? (candidate ? catalogRow(candidate, locationCode) : undefined);
    if (!standing && !source) {
      return;
    }
    const shelfRow = rowsRef.current.find((row) => row.locationCode === locationCode);
    const found: InventoryScreenRow =
      standing ??
      withVersion(
        withOnHand(
          {
            ...source!,
            locationCode,
            reserved: 0,
            ...(shelfRow?.pickable !== undefined ? { pickable: shelfRow.pickable } : {}),
          },
          0,
        ),
        0,
      );
    const key = keyOf(found);
    const stateKey = countStateKey(docId, key);
    foundRef.current[stateKey] = true;
    frozenRef.current[stateKey] = bookOf(found);
    delete prefilledRef.current[stateKey];
    delete persistedRef.current[stateKey];
    delete scheduledCountsRef.current[stateKey];
    if (!standing) {
      foundRowsRef.current = [...foundRowsRef.current.filter((row) => keyOf(row) !== key), found];
      setRows((previous) => (previous.some((row) => keyOf(row) === key) ? previous : [...previous, found]));
    }
    setAuditTargetsByShelf((current) => ({
      ...current,
      [locationCode]: [...new Set([...(current[locationCode] ?? []), sku])],
    }));
    setCounted((entries) => ({ ...entries, [key]: '' }));
    if (!standing) {
      setWalkFound((current) => ({ ...current, [key]: true }));
    }
    // The shelf is listed again with the found row among its rows, and the cursor waits in its count box.
    focusRowRef.current = key;
    setQuery(locationCode);
  };

  /**
   * A scan into the full count walk [SSD-7662]: a shelf label moves the walk to that shelf, and a product code goes to its
   * row on the shelf the counter stands at. A code naming several rows there asks which, and one naming none opens the
   * product search below, since the box may be on the wrong shelf or new to it.
   */
  /**
   * The 88 code that found a row is its barcode check [SSD-7662]. An uncounted row keeps it for its count; a counted row
   * without one is annotated now, so a row counted before its scan still reads as checked. A failed write leaves the row
   * unchecked and says so; the count itself is untouched.
   */
  const checkByScan = (row: InventoryScreenRow, scanned: string) => {
    const code = scannedBarcodeOf(scanned, row);
    if (!code) {
      return;
    }
    const key = keyOf(row);
    const check: StockTakeBarcodeCheckInput = { method: 'SCAN', code };
    const done = applied[key];
    const docId = openRoundsByShelf[row.locationCode];
    if (!done) {
      scanCheckRef.current[key] = check;
      return;
    }
    if (done.barcodeCheck || !docId) {
      return;
    }
    adminClient
      .annotateStockTakeLine(docId, { sku: row.sku, barcodeCheck: check })
      .then(() => {
        setApplied((standing) =>
          standing[key] ? { ...standing, [key]: { ...standing[key], barcodeCheck: check } } : standing,
        );
      })
      .catch(() => {
        setWalkStatus({ text: `${row.sku}: the barcode check did not save. Scan it again.`, kind: 'warn' });
      });
  };

  /** Selects the shelf code once it is back in the scan box, unless the counter has moved to another box meanwhile. */
  const settleAfterShelfBack = () => {
    window.setTimeout(() => {
      const holder = document.activeElement;
      if (holder && holder !== document.body && holder !== searchInput.ref.current) {
        return;
      }
      searchInput.settle();
    }, 0);
  };

  const scanIntoWalk = () => {
    const code = query.trim();
    setCodeChoice(null);
    if (!code) {
      searchInput.settle();
      return;
    }
    if (searchShelf !== null) {
      setProductLookup(null);
      const tallyOf = shelvesLeft.find((shelf) => shelf.locationCode === searchShelf);
      setWalkStatus(
        openRoundsByShelf[searchShelf]
          ? {
              text: `Shelf ${shelfLabel(searchShelf)}: ${
                tallyOf
                  ? `${tallyOf.left} of ${tallyOf.total} ${tallyOf.total === 1 ? 'row' : 'rows'} to count`
                  : 'nothing booked here'
              }. Scan a product.`,
              kind: 'info',
            }
          : { text: `Shelf ${shelfLabel(searchShelf)} is not in this walk. Add this shelf to count it.`, kind: 'warn' },
      );
      searchInput.settle();
      return;
    }
    const shelf = countingShelf;
    if (!shelf) {
      setWalkStatus({ text: 'Scan a shelf label first, then the products on it.', kind: 'warn' });
      searchInput.settle();
      return;
    }
    const named = rowsNamedOnShelf(code, shelf, sessionRows);
    // The shelf stays listed whatever the code turned out to be, so the counter never loses where they stand.
    setQuery(shelf);
    if (named.length === 1) {
      const [row] = named as [InventoryScreenRow];
      setProductLookup(null);
      checkByScan(row, code);
      focusRowRef.current = keyOf(row);
      setWalkStatus({
        text: `${row.sku}${row.name ? ` · ${row.name}` : ''}: type what is on the shelf and press Enter.`,
        kind: 'info',
      });
      return;
    }
    if (named.length > 1) {
      setProductLookup(null);
      choiceCodeRef.current = code;
      setCodeChoice(named.map(keyOf));
      setWalkStatus({
        text: `${code} names ${named.length} products on this shelf. Pick the one you hold.`,
        kind: 'warn',
      });
      // After the shelf code is back in the box: selecting the scanned code that it replaces leaves the shelf code
      // unselected, and the next scan would be typed onto its end.
      settleAfterShelfBack();
      return;
    }
    setProductLookup({ code, shelf });
    setWalkStatus({
      text: `${code} names no row on ${shelfLabel(shelf)}. Find it below to add it, or scan again.`,
      kind: 'warn',
    });
    settleAfterShelfBack();
  };

  /** The row the counter picked among those one code names; the cursor goes to its box. */
  const pickChoice = (row: InventoryScreenRow) => {
    setCodeChoice(null);
    if (choiceCodeRef.current) {
      checkByScan(row, choiceCodeRef.current);
      choiceCodeRef.current = null;
    }
    focusRowRef.current = keyOf(row);
    setWalkStatus({
      text: `${row.sku}${row.name ? ` · ${row.name}` : ''}: type what is on the shelf and press Enter.`,
      kind: 'info',
    });
  };

  /**
   * Previous or Next shelf in walk order [SSD-7662]. Rows left uncounted stay open and the band says so; at either end it
   * says it is the first or the last shelf and stays put. The cursor waits in the scan box for the next product.
   */
  const goShelf = (step: 1 | -1) => {
    const next = stepShelf(walkShelfList, countingShelf, step);
    if (!next) {
      setWalkStatus({
        text: step === 1 ? 'This is the last shelf of the walk.' : 'This is the first shelf of the walk.',
        kind: 'info',
      });
      searchInput.settle();
      return;
    }
    const leaving = countingShelf ? shelvesLeft.find((shelf) => shelf.locationCode === countingShelf) : undefined;
    const lastShelf = walkShelfList.indexOf(next) === walkShelfList.length - 1;
    const left = leaving && leaving.left > 0 ? leaving : null;
    setCodeChoice(null);
    setProductLookup(null);
    setCountingShelf(next);
    setQuery(next);
    setWalkStatus({
      text: [
        left
          ? `${shelfLabel(left.locationCode)}: ${left.left} ${left.left === 1 ? 'row' : 'rows'} not counted, still open.`
          : '',
        `Now on ${shelfLabel(next)}${lastShelf ? ', the last shelf' : ''}.`,
      ]
        .filter(Boolean)
        .join(' '),
      kind: left ? 'warn' : 'info',
    });
    window.setTimeout(() => searchInput.settle(), 0);
  };
  const goShelfRef = useRef(goShelf);
  goShelfRef.current = goShelf;
  // F7 and F8 step the full count walk, as the FC file did; a held key or one with a modifier is someone else's [SSD-7662].
  const fullCountOn = auditOn && isFullCount(auditGroupId);
  useEffect(() => {
    if (!fullCountOn) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.key !== 'F7' && event.key !== 'F8') ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.repeat
      ) {
        return;
      }
      event.preventDefault();
      goShelfRef.current(event.key === 'F8' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullCountOn]);

  /** Not on shelf: the row is counted at 0, and like any count off the book it waits for why [SSD-7662, SSD-7591]. */
  /**
   * Reads the given shelves' rounds again after a swap or a move [SSD-7662]: their counts, marks, found products and
   * targets replace what the screen held for those shelves only, and the stock is read again quietly, so the rest of the
   * walk (and any number being typed elsewhere) is left alone.
   */
  const rereadShelves = async (locationCodes: string[], keys: string[]) => {
    const touched = new Set(keys);
    const read = await Promise.all(
      locationCodes.flatMap((locationCode) => {
        const docId = openRoundsByShelf[locationCode];
        return docId ? [adminClient.getStockTake(docId).then((detail) => ({ locationCode, docId, detail }))] : [];
      }),
    );
    const appliedRead: Record<string, AppliedCount> = {};
    const countsRead: Record<string, string> = {};
    const foundRead: Record<string, true> = {};
    const targetsRead: Record<string, string[]> = {};
    read.forEach(({ locationCode, docId, detail }) => {
      const adoptedRaw = (detail.doc?.['meta'] as { foundSkus?: unknown } | undefined)?.foundSkus;
      const adopted = Array.isArray(adoptedRaw)
        ? adoptedRaw.filter((sku): sku is string => typeof sku === 'string')
        : [];
      adopted.forEach((sku) => {
        foundRead[`${sku}@${locationCode}`] = true;
      });
      targetsRead[locationCode] = [
        ...new Set([...(auditTargetsByShelf[locationCode] ?? []), ...adopted, ...detail.lines.map((line) => line.sku)]),
      ];
      detail.lines.forEach((line) => {
        if (line.countedQty === null || line.countedQty === undefined) {
          return;
        }
        const key = `${line.sku}@${locationCode}`;
        // Only the rows the swap or move wrote are replaced: a number being typed, a recount or a prompt elsewhere on
        // the shelf is the counter's, not this read's.
        if (!touched.has(key)) {
          return;
        }
        const value = String(line.countedQty);
        countsRead[key] = value;
        persistedRef.current[countStateKey(docId, key)] = value;
        savedCountsRef.current[countStateKey(docId, key)] = value;
        scheduledCountsRef.current[countStateKey(docId, key)] = value;
        if (line.applied) {
          const frozen = frozenRef.current[countStateKey(docId, key)];
          appliedRead[key] = {
            countedQty: line.countedQty,
            delta: line.diff,
            movedFromUnassigned: 0,
            at: line.appliedAt ?? null,
            ...(frozen?.version !== undefined ? { versionAfter: frozen.version } : {}),
            ...annotationsOfLine(line),
          };
        }
      });
    });
    setApplied((standing) => ({
      ...Object.fromEntries(Object.entries(standing).filter(([key]) => !touched.has(key))),
      ...appliedRead,
    }));
    setCounted((standing) => ({ ...standing, ...countsRead }));
    countedRef.current = { ...countedRef.current, ...countsRead };
    setWalkFound((standing) => ({ ...standing, ...foundRead }));
    setAuditTargetsByShelf((standing) => ({ ...standing, ...targetsRead }));
    await load(showZeroQty, undefined, true);
  };

  /**
   * A swap or move refused as stale: the rows it names are marked to count again and the stock is read again, so the next
   * try is made against the shelf as it stands rather than the same stale book [SSD-7662].
   */
  const refuseStale = (docId: string, locationCode: string, cause: unknown) => {
    const refusal = staleStockRefusalOf(cause);
    if (refusal) {
      markStaleRecounts(docId, locationCode, refusal.staleLines);
    }
    void load(showZeroQty, undefined, true);
  };

  /**
   * One key per swap or move draft [SSD-7662]: a retry after a lost answer sends the same key, so the server answers with
   * the first write instead of refusing the row it already changed. A success or a refusal ends the draft.
   */
  const actionKeysRef = useRef<Record<string, string>>({});
  const actionKeyOf = (draft: string): string => {
    actionKeysRef.current[draft] ??= uuidv4();
    return actionKeysRef.current[draft];
  };
  /** Whether a failure was a definite answer from the server (4xx) rather than a lost one. */
  const refusedByServer = (cause: unknown): boolean => {
    const status = (cause as { response?: { status?: number } })?.response?.status;
    return status !== undefined && status >= 400 && status < 500;
  };

  /** The error code a refused stock take write answers with, when it carries one. */
  const refusalCodeOf = (cause: unknown): string | undefined =>
    (cause as { response?: { data?: { code?: unknown } } })?.response?.data?.code as string | undefined;

  /** Writes a name check, a note or a barcode correction on a counted walk row [SSD-7662]. */
  const annotateRow = async (
    row: InventoryScreenRow,
    patch: { barcodeCheck?: StockTakeBarcodeCheckInput; note?: string; barcodeCorrection?: string },
    said: string,
  ) => {
    const docId = openRoundsByShelf[row.locationCode];
    const key = keyOf(row);
    if (!docId) {
      return;
    }
    setAuditBusy(true);
    try {
      await adminClient.annotateStockTakeLine(docId, { sku: row.sku, ...patch });
      setApplied((standing) => {
        const entry = standing[key];
        if (!entry) {
          return standing;
        }
        const next: AppliedCount = { ...entry };
        if (patch.barcodeCheck) {
          next.barcodeCheck = patch.barcodeCheck;
        }
        if (patch.note !== undefined) {
          next.note = patch.note || null;
        }
        if (patch.barcodeCorrection !== undefined) {
          next.barcodeCorrection = patch.barcodeCorrection || null;
        }
        return { ...standing, [key]: next };
      });
      setWalkStatus({ text: `${row.sku}: ${said}`, kind: 'ok' });
    } catch (cause) {
      if (refusalCodeOf(cause) === 'STOCK_TAKE_LINE_NOT_COUNTED') {
        showError(`${row.sku} is not counted yet. Count it first, then add this.`);
        return;
      }
      showFailure(cause, 'This could not be saved.');
    } finally {
      setAuditBusy(false);
    }
  };

  /**
   * Swap: the shelf holds another product where the row says this one [SSD-7662]. The server counts the row to 0 and
   * the product found at its count in one save, so the stock is never half swapped.
   */
  const swapRow = async (row: InventoryScreenRow, foundSku: string, foundQty: number, reason: AdjustReason) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId) {
      return;
    }
    const frozen = frozenRef.current[countStateKey(docId, keyOf(row))] ?? bookOf(row);
    const draft = `swap|${keyOf(row)}|${foundSku}|${foundQty}|${reason}`;
    setAuditBusy(true);
    try {
      const result = await adminClient.swapStockTakeLine(docId, {
        rowSku: row.sku,
        foundSku,
        foundQty,
        reason,
        idempotencyKey: actionKeyOf(draft),
        expectedSystemQty: frozen.onHand,
        ...(frozen.version !== undefined ? { expectedVersion: frozen.version } : {}),
      });
      delete actionKeysRef.current[draft];
      const foundKey = `${foundSku}@${row.locationCode}`;
      // Both halves are frozen at what the swap left, so a recount on either is not refused as stale [SSD-7662].
      [
        [keyOf(row), result.row],
        [foundKey, result.found],
      ].forEach(([key, half]) => {
        const written = half as CountStockTakeResult;
        if (written.versionAfter !== undefined) {
          frozenRef.current[countStateKey(docId, key as string)] = {
            onHand: written.onHandAfter ?? written.countedQty ?? 0,
            version: written.versionAfter,
          };
        } else {
          delete frozenRef.current[countStateKey(docId, key as string)];
        }
      });
      setMoreRow(null);
      await rereadShelves([row.locationCode], [keyOf(row), foundKey]);
      setWalkStatus({
        text: `${row.sku} swapped for ${foundSku} on ${shelfLabel(row.locationCode)}: ${row.sku} at 0, ${foundSku} at ${foundQty}.`,
        kind: 'ok',
      });
    } catch (cause) {
      if (refusedByServer(cause)) {
        delete actionKeysRef.current[draft];
      }
      const code = refusalCodeOf(cause);
      if (code === 'SWAP_FOUND_SKU_ON_SHELF') {
        showError(`${foundSku} already has a row on ${shelfLabel(row.locationCode)}. Count that row instead.`);
      } else if (code === 'STALE_STOCK') {
        refuseStale(docId, row.locationCode, cause);
        showError(`${row.sku} moved on the shelf since counting began. Recount it, then swap.`);
      } else if (code === 'BELOW_RESERVED') {
        showError(
          `Orders reserve more than the swap leaves on ${shelfLabel(row.locationCode)}. Count the rows instead.`,
        );
      } else if (code === 'STOCK_TAKE_SKU_NOT_TARGETED') {
        showError(`${row.sku} is not saved on this shelf yet. Count it first, then swap.`);
      } else {
        showFailure(cause, 'The swap could not be saved.');
      }
    } finally {
      setAuditBusy(false);
    }
  };

  /**
   * Move: the row's whole stock goes to another shelf of the walk, with its count [SSD-7662]. Both shelves are frozen at
   * the versions the move left, so the next count on either is not refused as stale.
   */
  const moveRow = async (row: InventoryScreenRow, toShelf: string) => {
    const docId = openRoundsByShelf[row.locationCode];
    const toDocId = openRoundsByShelf[toShelf];
    if (!docId || !toDocId) {
      return;
    }
    const frozen = frozenRef.current[countStateKey(docId, keyOf(row))] ?? bookOf(row);
    const draft = `move|${keyOf(row)}|${toShelf}`;
    setAuditBusy(true);
    try {
      const result = await adminClient.moveStockTakeStock(docId, {
        sku: row.sku,
        toDocId,
        idempotencyKey: actionKeyOf(draft),
        expectedSystemQty: frozen.onHand,
        ...(frozen.version !== undefined ? { expectedVersion: frozen.version } : {}),
        reason: `Full count walk: moved to ${shelfLabel(toShelf)}`,
      });
      delete actionKeysRef.current[draft];
      frozenRef.current[countStateKey(docId, keyOf(row))] = {
        onHand: result.source.onHandAfter,
        version: result.source.versionAfter,
      };
      frozenRef.current[countStateKey(toDocId, `${row.sku}@${toShelf}`)] = {
        onHand: result.destination.onHandAfter,
        version: result.destination.versionAfter,
      };
      setMoreRow(null);
      await rereadShelves([row.locationCode, toShelf], [keyOf(row), `${row.sku}@${toShelf}`]);
      setWalkStatus({
        text: `${row.sku}: ${result.qty} moved from ${shelfLabel(row.locationCode)} to ${shelfLabel(toShelf)}.`,
        kind: 'ok',
      });
    } catch (cause) {
      if (refusedByServer(cause)) {
        delete actionKeysRef.current[draft];
      }
      const code = refusalCodeOf(cause);
      if (code === 'MOVE_DESTINATION_HOLDS_SKU') {
        showError(`${shelfLabel(toShelf)} already holds ${row.sku}. Count both rows instead of moving.`);
      } else if (code === 'MOVE_SOURCE_EMPTY') {
        showError(`${row.sku} has nothing on ${shelfLabel(row.locationCode)} to move.`);
      } else if (code === 'STALE_STOCK') {
        refuseStale(docId, row.locationCode, cause);
        showError(`${row.sku} moved on the shelf since counting began. Recount it, then move.`);
      } else if (code === 'MOVE_DESTINATION_OUTSIDE_AUDIT' || code === 'STOCK_TAKE_SKU_NOT_TARGETED') {
        showError(`${row.sku} cannot be moved to ${shelfLabel(toShelf)} in this walk.`);
      } else {
        showFailure(cause, 'The move could not be saved.');
      }
    } finally {
      setAuditBusy(false);
    }
  };

  const markNotOnShelf = (row: InventoryScreenRow) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId) {
      return;
    }
    const key = keyOf(row);
    frozenRef.current[countStateKey(docId, key)] ??= bookOf(row);
    countedRef.current = { ...countedRef.current, [key]: '0' };
    setCounted((entries) => ({ ...entries, [key]: '0' }));
    setWalkStatus({ text: `${row.sku}: pick why it is not on the shelf to save it at 0.`, kind: 'warn' });
    void persistCount(docId, row, '0').catch(() => undefined);
  };

  /**
   * Recount: the row's box is emptied and the cursor goes back into it [SSD-7662]. The next number entered is a new count
   * against the stock as it stands then, written even when it is the number saved before; leaving the box empty writes
   * nothing, so a stray click cannot overwrite what moved on the shelf since.
   */
  const recountRow = (row: InventoryScreenRow, index: number) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId) {
      return;
    }
    const key = keyOf(row);
    const stateKey = countStateKey(docId, key);
    delete persistedRef.current[stateKey];
    delete scheduledCountsRef.current[stateKey];
    delete applyKeysRef.current[stateKey];
    delete countReasonRef.current[stateKey];
    frozenRef.current[stateKey] = bookOf(row);
    recountedRef.current.add(key);
    countedRef.current = { ...countedRef.current, [key]: '' };
    setCounted((entries) => ({ ...entries, [key]: '' }));
    setWalkStatus({ text: `${row.sku}: count it again and press Enter.`, kind: 'info' });
    const input = countInputsRef.current[index];
    input?.focus();
  };

  /** A product found in the search below, added to the shelf at 0 for the counter to count [SSD-7662]. */
  const addCandidate = (candidate: ShelfCandidate) => {
    const shelf = productLookup?.shelf ?? countingShelf;
    if (!shelf) {
      return;
    }
    setProductLookup(null);
    setWalkStatus({
      text: `${candidate.sku} added to ${shelfLabel(shelf)}: type what is there and press Enter.`,
      kind: 'ok',
    });
    void addFoundRow(candidate.sku, shelf, candidate);
  };

  const leaveAudit = () => {
    // closedRoundsRef is kept: a refused write still in flight for a round of this walk must stay quiet.
    liftedStaleRef.current = {};
    scanCheckRef.current = {};
    choiceCodeRef.current = null;
    setMoreRow(null);
    shelfKeysRef.current.clear();
    recountedRef.current.clear();
    typedReasonsRef.current = {};
    setTypedReasons({});
    persistedRef.current = {};
    savedCountsRef.current = {};
    scheduledCountsRef.current = {};
    countSaveFailuresRef.current = {};
    prefilledRef.current = {};
    frozenRef.current = {};
    applyKeysRef.current = {};
    shortfallAckRef.current = {};
    foundRef.current = {};
    // A found row never saved has no stock behind it, so it goes with the walk.
    const unsaved = new Set(foundRowsRef.current.map(keyOf));
    foundRowsRef.current = [];
    if (unsaved.size > 0) {
      setRows((previous) => previous.filter((row) => !unsaved.has(keyOf(row))));
    }
    setStaleRecounts({});
    setApplied({});
    setShortfallPrompts({});
    setShortfallReasons({});
    setReasonPrompts({});
    countReasonRef.current = {};
    setKeepUnassigned({});
    setCountingShelf(null);
    setCounted({});
    setOpenRoundsByShelf({});
    setAuditTargetsByShelf({});
    setAuditGroupId(uuidv4());
    setWalkStatus(null);
    setCodeChoice(null);
    setProductLookup(null);
    setWalkHistoryOpen(false);
    setWalkStartedAt(null);
    setWalkFound({});
    setAuditOn(false);
  };

  /** Takes one shelf out of the walk: its round is confirmed, or closed by someone else, either way no longer ours to send. */
  const dropShelfRound = (locationCode: string) => {
    const docId = openRoundsByShelf[locationCode];
    if (docId) {
      // A write still queued for it is dropped, and one already out that comes back "closed" says nothing more.
      closedRoundsRef.current.add(docId);
    }
    // A recount hold on this shelf, standing or lifted, belonged to its old round; a new round starts without it.
    const onShelf = (key: string) => key.endsWith(`@${locationCode}`);
    Object.keys(liftedStaleRef.current)
      .filter(onShelf)
      .forEach((key) => delete liftedStaleRef.current[key]);
    setStaleRecounts((standing) => {
      const kept = Object.entries(standing).filter(([key]) => !onShelf(key));
      return kept.length === Object.keys(standing).length ? standing : Object.fromEntries(kept);
    });
    shelfKeysRef.current.delete(locationCode);
    Object.keys(scanCheckRef.current)
      .filter((key) => key.endsWith(`@${locationCode}`))
      .forEach((key) => delete scanCheckRef.current[key]);
    setOpenRoundsByShelf((current) => {
      const next = { ...current };
      delete next[locationCode];
      return next;
    });
    setAuditTargetsByShelf((current) => {
      const next = { ...current };
      delete next[locationCode];
      return next;
    });
  };

  /**
   * A count or confirm landed on a round a colleague had already confirmed or abandoned [SSD-7209]: the server's
   * sentence is shown once per round however many of its writes were refused, the dialog closes, the shelf leaves
   * the walk, and the book is read again. The walk ends when no round of it is left open, counting the rounds
   * refused in the same flush that this render has not dropped yet.
   */
  const closedRoundRefused = (locationCode: string, refusal: ClosedStockTakeRefusal, docId: string) => {
    if (closedRoundsRef.current.has(docId)) {
      return;
    }
    closedRoundsRef.current.add(docId);
    showError(`Shelf ${shelfLabel(locationCode)}: ${describeClosedStockTake(refusal)}`);
    setFinishingAudit(false);
    dropShelfRound(locationCode);
    const stillOpen = Object.entries(openRoundsByShelf).filter(
      ([shelf, roundId]) => shelf !== locationCode && !closedRoundsRef.current.has(roundId),
    );
    if (stillOpen.length === 0) {
      leaveAudit();
    }
    void load(showZeroQty);
  };
  closedRoundRefusedRef.current = closedRoundRefused;

  /**
   * Closes every open round as abandoned, so the walk is on record [D-n]. Counts already saved are in the stock and stay
   * there; only rows nobody counted are left as the book shows [SSD-7447]. A shelf whose round a colleague already
   * confirmed or closed has nothing left to close: it leaves the walk quietly and the other shelves are still closed
   * [SSD-7436]. Only a real refusal keeps the walk open.
   */
  const abandonAudit = async () => {
    setAuditBusy(true);
    const closedElsewhere: string[] = [];
    let refusal: unknown = null;
    let inStock = 0;
    let reported = false;
    try {
      for (const [locationCode, docId] of Object.entries(openRoundsByShelf)) {
        try {
          const closed = await adminClient.cancelStockTake(docId, ABANDON_REASON);
          if (closed.appliedLines !== undefined) {
            inStock += closed.appliedLines;
            reported = true;
          }
        } catch (failure) {
          if (!isRoundAlreadyClosed(failure)) {
            refusal = refusal ?? failure;
            continue;
          }
          closedElsewhere.push(shelfLabel(locationCode));
        }
        dropShelfRound(locationCode);
      }
      if (refusal) {
        showFailure(refusal, 'The audit could not be closed.');
        return;
      }
      const kept = reported ? inStock : countedRows;
      leaveAudit();
      const outcome =
        kept > 0
          ? `The stock audit was closed. ${kept} counted ${kept === 1 ? 'row stays' : 'rows stay'} in the stock; rows not counted keep what the book shows.`
          : 'The stock audit was closed; nothing had been counted, so no stock changed.';
      showWarning(
        closedElsewhere.length > 0
          ? `${outcome} Already confirmed by someone else, so left as it was: shelf ${closedElsewhere.join(', ')}.`
          : outcome,
      );
    } finally {
      setAuditBusy(false);
      setExiting(false);
      // Counts already saved and a colleague's confirm both moved the book, so the list is read again.
      void load(showZeroQty);
    }
  };

  /**
   * Closes the walk [SSD-7447]. Every counted row is already in the stock, so each shelf's round is sealed as it stands:
   * a row nobody counted keeps the book's stock and is named in the outcome, to be counted in the next audit. Each shelf
   * is sealed on its own (SSD-7212, decision D8): a refused shelf stays open and the others are still finished. A lost
   * answer is the one exception: whether that shelf was sealed is unknown, so the walk stops there and says so.
   */
  const finishAudit = async () => {
    if (finishingAuditRef.current) {
      return;
    }
    finishingAuditRef.current = true;
    setAuditBusy(true);
    const openRounds = Object.entries(openRoundsByShelf);
    const shelves = openRounds.map(([locationCode]) => `Shelf ${shelfLabel(locationCode)}`);
    let finished = 0;
    let dropped = 0;
    let countsFlushed = false;
    const finishedShelves: string[] = [];
    const failed: BulkItemReason[] = [];
    const uncounted: string[] = [];
    /** Random check shelves nobody counted a row on: closed as abandoned so the next draw still treats them as unchecked. */
    const notChecked: string[] = [];
    const randomWalk = isRandomCheck(auditGroupId);
    let serverListed = false;
    try {
      await flushCounts();
      countsFlushed = true;
      if (openRounds.every(([, docId]) => closedRoundsRef.current.has(docId))) {
        // Every shelf was closed by someone else while its last counts went: each said so, and the walk has ended.
        return;
      }
      for (const [index, [locationCode, docId]] of openRounds.entries()) {
        if (closedRoundsRef.current.has(docId)) {
          // Closed by a colleague while its counts were flushed; that shelf has already said so.
          dropped += 1;
          continue;
        }
        // Confirming a drawn shelf nobody counted would file it as checked for two weeks; it was not [SSD-7583].
        if (
          randomWalk &&
          !sessionRows.some((row) => row.locationCode === locationCode && applied[keyOf(row)] !== undefined)
        ) {
          try {
            await adminClient.cancelStockTake(docId, RANDOM_NOT_CHECKED_REASON);
          } catch (failure) {
            if (!isRoundAlreadyClosed(failure)) {
              failed.push({
                item: shelves[index]!,
                reason: describeFailure(failure, 'The shelf could not be closed.'),
              });
              continue;
            }
          }
          notChecked.push(shelfLabel(locationCode));
          dropShelfRound(locationCode);
          continue;
        }
        try {
          const sealed = await adminClient.confirmStockTake(docId);
          if (Array.isArray(sealed.uncountedSkus)) {
            serverListed = true;
            sealed.uncountedSkus.forEach((sku) => uncounted.push(`${sku} on ${shelfLabel(locationCode)}`));
          }
        } catch (failure) {
          if (axios.isAxiosError(failure) && (!failure.response || failure.response.status >= 500)) {
            // Whether this shelf was sealed is unknown, so the walk stops here; what came before is reported as it was.
            const outcome = interruptedSequenceResult('Stock Audit', {
              confirmed: finishedShelves,
              failed,
              unknown: shelves[index]!,
              notAttempted: shelves.slice(index + 1),
            });
            showError(outcome.text, outcome.details);
            setFinishingAudit(false);
            await load(showZeroQty);
            return;
          }
          const refusal = staleStockRefusalOf(failure);
          const closed = closedStockTakeRefusalOf(failure);
          if (refusal) {
            // A count saved before the live audit is applied only here; its shelf moved, so it is counted again.
            markStaleRecounts(docId, locationCode, refusal.staleLines);
            failed.push({
              item: shelves[index]!,
              reason: `${describeStaleStock(refusal.staleLines)} Count these items again.`,
            });
          } else if (closed) {
            // A colleague sealed or abandoned this shelf's round, so there is nothing left on it to close.
            dropShelfRound(locationCode);
            dropped += 1;
            failed.push({ item: shelves[index]!, reason: describeClosedStockTake(closed) });
          } else {
            failed.push({
              item: shelves[index]!,
              reason: describeFailure(failure, 'The shelf could not be closed.'),
            });
          }
          continue;
        }
        finished += 1;
        finishedShelves.push(shelves[index]!);
        dropShelfRound(locationCode);
      }
      setFinishingAudit(false);
      if (!serverListed) {
        // An older server lists nothing; the rows this screen never saw counted are the ones left.
        sessionRows
          .filter((row) => applied[keyOf(row)] === undefined)
          .forEach((row) => uncounted.push(`${row.sku} on ${shelfLabel(row.locationCode)}`));
      }
      const left =
        uncounted.length > 0
          ? ` ${uncounted.length} ${
              uncounted.length === 1 ? 'row was not counted and keeps' : 'rows were not counted and keep'
            } the stock the book shows.`
          : notChecked.length > 0
            ? ` Every row on ${finished === 1 ? 'it' : 'them'} was counted.`
            : ' Every row was counted.';
      const skippedShelves =
        notChecked.length > 0 ? ` Not checked, so left for the next Random check: shelf ${notChecked.join(', ')}.` : '';
      if (failed.length === 0) {
        leaveAudit();
        const text = `Stock Audit finished — ${finished} ${finished === 1 ? 'shelf' : 'shelves'}.${left}${skippedShelves}`;
        if (uncounted.length > 0 || notChecked.length > 0) {
          showWarning(text, uncounted);
        } else {
          showSuccess(text);
        }
        await load(showZeroQty);
        return;
      }
      const names = failed.map((entry) => entry.item.replace(/^Shelf /, '')).join(', ');
      const text = `Stock Audit: ${finished} of ${openRounds.length} shelves finished, ${failed.length} not (${names}). Finishing again closes only the shelves still open.`;
      const details = failed.map(({ item, reason }) => `${item} — ${reason}`);
      if (finished > 0) {
        showWarning(text, details);
      } else {
        showError(text, details);
      }
      if (finished + dropped + notChecked.length === openRounds.length) {
        // Every shelf is either finished or closed by someone else: nothing is left to walk.
        leaveAudit();
      }
      await load(showZeroQty);
    } catch (failure) {
      // The last saves failed: a refused or closed count already said what happened; anything else is said here.
      if (!countsFlushed) {
        setFinishingAudit(false);
        if (!staleStockRefusalOf(failure) && !closedStockTakeRefusalOf(failure)) {
          showFailure(failure, 'The audit was not finished — a count could not be saved. Check the highlighted rows.');
        }
      }
    } finally {
      finishingAuditRef.current = false;
      setAuditBusy(false);
    }
  };

  /** What a move came to: applied, refused by the server (the box closes on the shelf it still holds), or never known
   * to have arrived (the box stays with its key, so the retry is the same request) [SSD-7527]. */
  type MoveOutcome = 'applied' | 'refused' | 'unreachable' | 'duplicate';
  /**
   * The one request still unanswered per row, with its retry key: the same ask again carries the same key, and a
   * different ask on that row starts fresh and forgets the old one, so an old key is never replayed as a new move.
   */
  const moveKeysRef = useRef(new Map<string, { ask: string; key: string }>());

  const relocate = async (row: InventoryScreenRow, toLocation: string, units = row.onHand): Promise<MoveOutcome> => {
    // Enter and ✓ can both land before the box closes, so without this one save could move the stock twice.
    if (relocatingRef.current.has(keyOf(row))) {
      return 'duplicate';
    }
    relocatingRef.current.add(keyOf(row));
    setMovesInFlight((count) => count + 1);
    const partial = units < row.onHand;
    const ask = [row.sku, row.locationCode, toLocation, units].join('\u0000');
    const pending = moveKeysRef.current.get(keyOf(row));
    const idempotencyKey = pending && pending.ask === ask ? pending.key : uuidv4();
    moveKeysRef.current.set(keyOf(row), { ask, key: idempotencyKey });
    const from = shelfLabel(row.locationCode);
    try {
      // The whole shelf moves unless some units were asked for, so the orders holding it move with it rather than
      // refusing the move [SSD-7446]; a partial move leaves the reservations on the shelf, and the server refuses one
      // that would leave fewer units than are reserved.
      const result = await adminClient.moveStock({
        sku: row.sku,
        fromLocation: row.locationCode,
        toLocation,
        qty: units,
        idempotencyKey,
      });
      moveKeysRef.current.delete(keyOf(row));
      if (!result.moved) {
        showError(`The location change was not applied — ${row.sku} stays on ${from}.`);
        return 'refused';
      }
      const moved = partial ? `${units} of ${row.onHand} ${row.sku}` : row.sku;
      const next = await load(
        showZeroQty,
        `Location saved — ${moved}: ${from} → ${shelfLabel(toLocation)}.${reservationsMovedText(result.reservationsMoved)}`,
      );
      // The move can empty the line the list is narrowed to, and a filter over nothing reads as lost stock [D-p].
      if (line !== 'all' && next && !next.some((entry) => inLine(entry, line))) {
        setLine('all');
      }
      return 'applied';
    } catch (failure) {
      // A request the server never answered may still have landed: the key is kept for the retry. A refusal the
      // server did answer closes the box on the shelf it still holds, and must not read as an applied move.
      // No response, or a 5xx: the server may have applied the move before failing to say so (the Location Import reads
      // these the same way), so the key stays for the retry.
      if (axios.isAxiosError(failure) && (!failure.response || failure.response.status >= 500)) {
        showFailure(
          failure,
          'The location change could not be confirmed — send the same move again, or close the box to re-read the shelf.',
        );
        return 'unreachable';
      }
      moveKeysRef.current.delete(keyOf(row));
      showFailure(failure, 'The location change was refused.');
      return 'refused';
    } finally {
      relocatingRef.current.delete(keyOf(row));
      setMovesInFlight((count) => count - 1);
    }
  };

  const loadHolders = useCallback(
    async (sku: string, locationCode: string) => (await adminClient.listReservationHolders(sku, locationCode)).holders,
    [adminClient],
  );

  const showHolders = async (sku: string, locationCode: string) => {
    const requestId = ++reservedRequestRef.current;
    reservedSkuRef.current = sku;
    setReservedSku(sku);
    setReservedLocation(locationCode);
    setHolders([]);
    setHoldersFailed(false);
    setHoldersLoading(true);
    try {
      // A sku reserved on two shelves has two holder sets, so the shelf rides on the wire [T13].
      const result = await adminClient.listReservationHolders(sku, locationCode);
      if (requestId === reservedRequestRef.current && reservedSkuRef.current === sku) {
        setHolders(result.holders);
      }
    } catch {
      // A failed read is a finding, never the empty state: "nobody holds this" would be a lie.
      if (requestId === reservedRequestRef.current) {
        setHoldersFailed(true);
      }
    } finally {
      if (requestId === reservedRequestRef.current) {
        setHoldersLoading(false);
      }
    }
  };

  const release = async (holder: ReservationHolder) => {
    const sku = reservedSkuRef.current;
    if (!releaseDraft.reason.trim() || releaseBusy || !sku || releaseInFlightRef.current) {
      return;
    }
    releaseInFlightRef.current = true;
    const restockQty = Number(releaseDraft.restockQty.trim());
    setReleaseBusy(`${holder.orderId}-${holder.lineNo}`);
    try {
      await adminClient.releaseReservation({
        orderId: holder.orderId,
        lineNo: holder.lineNo,
        reason: releaseDraft.reason.trim(),
        restock: releaseDraft.restock,
        // A write-off names neither a shelf nor a quantity: the units are not going anywhere.
        ...(releaseDraft.restock
          ? {
              ...(releaseDraft.restockQty.trim() && Number.isFinite(restockQty) ? { restockQty } : {}),
              locationCode: reservedLocation,
            }
          : {}),
      });
      await showHolders(sku, reservedLocation);
      await load(showZeroQty, `Reservation released — order ${holder.orderId}, line ${holder.lineNo}.`);
    } catch (failure) {
      showFailure(failure, 'The release was refused.');
    } finally {
      releaseInFlightRef.current = false;
      setReleaseBusy(null);
    }
  };

  /**
   * Reads a page of the audit log; the first page replaces the list and a later one is appended under the same filter.
   * Closing, reopening or refiltering starts a new listing, and a page that answers for an older one is dropped so it
   * cannot append stale rounds or overwrite the cursor.
   */
  const loadRounds = async (origin: StockTakeOrigin | 'all', before: string | null) => {
    const listing = roundsListingRef.current;
    setRoundsLoading(true);
    try {
      const result = await adminClient.listStockTakes({
        limit: ROUNDS_PAGE_SIZE,
        ...(origin !== 'all' ? { origin } : {}),
        ...(before ? { before } : {}),
      });
      if (listing !== roundsListingRef.current) {
        return;
      }
      setRounds((shown) => (before ? [...shown, ...result.rounds] : result.rounds));
      setRoundsCursor(result.nextCursor ?? null);
    } catch {
      if (listing === roundsListingRef.current) {
        showError('Could not read the audit rounds.');
      }
    } finally {
      if (listing === roundsListingRef.current) {
        setRoundsLoading(false);
      }
    }
  };

  const [closingRounds, setClosingRounds] = useState(false);
  /**
   * Closes the open shelves of an audit that is not this walk — a colleague's tab left open, or a round from the retired
   * Stock Take screen — so they are free to count or edit again [SSD-7447]. Counts already saved stay in the stock.
   */
  const closeOpenRounds = async (shelves: StockTakeRound[]) => {
    setClosingRounds(true);
    const refused: string[] = [];
    let kept = 0;
    try {
      for (const shelf of shelves) {
        try {
          const closed = await adminClient.cancelStockTake(shelf.docId, 'closed from Past Audit Logs');
          kept += closed.appliedLines ?? 0;
        } catch (failure) {
          if (!isRoundAlreadyClosed(failure)) {
            refused.push(shelfLabel(shelf.locationCode));
          }
        }
      }
    } finally {
      setClosingRounds(false);
    }
    if (refused.length > 0) {
      showError(`Shelf ${refused.join(', ')} could not be closed.`);
    } else {
      showSuccess(
        `Closed ${shelves.length} open ${shelves.length === 1 ? 'shelf' : 'shelves'}.${
          kept > 0 ? ` ${kept} counted ${kept === 1 ? 'row stays' : 'rows stay'} in the stock.` : ''
        }`,
      );
    }
    setRounds([]);
    setRoundsCursor(null);
    await loadRounds(roundsOrigin, null);
    void load(showZeroQty);
  };

  const closeRounds = () => {
    roundsListingRef.current += 1;
    setRoundsLoading(false);
    setShowRounds(false);
  };

  const openRounds = async () => {
    roundsListingRef.current += 1;
    setShowRounds(true);
    setRounds([]);
    setRoundsCursor(null);
    await loadRounds(roundsOrigin, null);
  };

  const filterRounds = (origin: StockTakeOrigin | 'all') => {
    roundsListingRef.current += 1;
    setRoundsOrigin(origin);
    setRounds([]);
    setRoundsCursor(null);
    void loadRounds(origin, null);
  };

  const exportStockStatus = async (changesOnly = false) => {
    if (exportInFlightRef.current) {
      return;
    }
    exportInFlightRef.current = true;
    try {
      // Mid-audit the export is the walk itself: the filtered rows with their counts and diffs [SSD-7212]. Otherwise
      // the endpoint answers JSON and the download is named .csv, so the screen converts before saving. A full count walk
      // writes the FC file's own columns, the whole walk or only its changes [SSD-7662].
      const csv = fullCount
        ? fullCountCsv(sessionRows, applied, walkFoundSet, changesOnly)
        : auditOn
          ? auditCsv(auditRows, counted)
          : stockStatusCsv(await stockClient.exportStockStatusRows());
      if (csv === null) {
        showError(
          fullCount && changesOnly
            ? 'Nothing in this walk has changed yet.'
            : auditOn
              ? 'There are no rows in this audit to export.'
              : 'There is no stock to export.',
          undefined,
          AUTO_DISMISS,
        );
        return;
      }
      if (typeof URL.createObjectURL !== 'function') {
        showError('This browser cannot start the stock export download.');
        return;
      }
      const href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = fullCount
        ? changesOnly
          ? 'full-count-changes.csv'
          : 'full-count-walk.csv'
        : auditOn
          ? 'stock-audit.csv'
          : 'stock-status.csv';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoking in the same tick can cancel the download the click just started.
      setTimeout(() => URL.revokeObjectURL(href), 0);
      showSuccess(auditOn ? 'Audit export download started.' : 'Stock export download started.');
    } catch {
      showError('The export failed.');
    } finally {
      exportInFlightRef.current = false;
    }
  };

  /**
   * Enter commits the count and drops to the next shelf; Escape puts the cell back to the last saved
   * reading. The floor counts with a scanner in one hand, so the column has to be walked without a mouse.
   */
  const onCountKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, row: InventoryScreenRow, index: number) => {
    const key = keyOf(row);
    if (event.key === 'Enter') {
      event.preventDefault();
      saveCount(row, true);
      if (fullCount) {
        // Counted in the order the products are scanned: what comes next is the next product's barcode [SSD-7662].
        searchInput.settle();
        return;
      }
      const next = countInputsRef.current[index + 1];
      if (next) {
        // Selected, so the count typed next replaces the book value rather than landing after it.
        next.focus();
        next.select();
      } else {
        // The last row on screen: the next thing read is the next shelf's label, so it goes to the search [SSD-7529].
        searchInput.settle();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      const docId = openRoundsByShelf[row.locationCode];
      // The count was put back, so counting this row begins again, against the book at that time [SSD-7447].
      if (docId) {
        delete frozenRef.current[countStateKey(docId, key)];
        // The number put back is not the one a reason was asked or picked for [SSD-7591].
        delete countReasonRef.current[countStateKey(docId, key)];
      }
      setReasonPrompts((standing) => {
        if (!standing[key]) {
          return standing;
        }
        const rest = { ...standing };
        delete rest[key];
        return rest;
      });
      setCounted((entries) => ({
        ...entries,
        [key]: (docId && persistedRef.current[countStateKey(docId, key)]) ?? String(row.onHand),
      }));
    }
  };

  /**
   * Enter in the search. In a walk it goes to the first Counted Qty on screen, so a scanned shelf is counted without the
   * mouse; anywhere else the code stays selected so the next scan replaces it [SSD-7529].
   */
  const onSearchEnter = () => {
    if (fullCount) {
      scanIntoWalk();
      return;
    }
    const first = auditOn && shownRows.length > 0 ? countInputsRef.current[0] : null;
    if (first && !first.disabled) {
      first.focus();
      first.select();
    } else {
      searchInput.settle();
    }
  };
  /** The latest `onSearchEnter`, for the turn after a composition ends, when the rows it reads have been re-rendered. */
  const onSearchEnterRef = useRef(onSearchEnter);
  onSearchEnterRef.current = onSearchEnter;

  const tabs: Array<[InventoryTab, string]> = [
    ['current', 'Current Stocks'],
    ['history', 'Stock History'],
    ['inbound', 'Inbound Stock'],
    ['outbound', 'Stock Adjustment'],
  ];

  return (
    <div className="wms-wireframe ff-ultimate wms-inventory">
      <div className="ptitle">
        <h2>WMS — Inventory</h2>
        <span className="sp" />
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label="Refresh the stock list"
          disabled={loading}
          onClick={() => {
            setSortEpoch((epoch) => epoch + 1);
            void load(showZeroQty);
          }}
        >
          <RefreshIcon />
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-gray btn-sm"
          aria-label={auditOn ? 'Export Audit' : 'Export Stock Status'}
          onClick={() => void exportStockStatus()}
        >
          <DownloadIcon />
          {auditOn ? 'Export Audit' : 'Export Stock Status'}
        </button>
        {tab === 'current' && (
          <button
            type="button"
            className="btn btn-line btn-sm"
            aria-label="Import Locations"
            /* A walk counts shelves as they stand, so nothing moves them in bulk mid-audit. */
            disabled={auditOn}
            onClick={() => setImportingLocations(true)}
          >
            <UploadIcon />
            Import Locations
          </button>
        )}
      </div>

      <div className="subtabs">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`${key === 'current' ? 'newtab ' : ''}${tab === key ? 'on' : ''}`.trim()}
            /* The open tab clicked again keeps its search; another tab opens empty, as it always has. */
            onClick={() => key !== tab && showInAddress(key, null)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div role="alert" className="note warn">
          {error}
        </div>
      )}

      {tab === 'current' && (
        <>
          {needsFixing.length > 0 && (
            <NeedsFixingFirst
              rows={needsFixing}
              auditOn={auditOn}
              openRoundsByShelf={openRoundsByShelf}
              auditBusy={walkLocked}
              fixingKey={fixing}
              savedKey={savedKey}
              busy={adjustBusy}
              onToggleFix={(row) => {
                setSavedKey(null);
                setAdjusting(null);
                setEditingShelf(null);
                setFixing(fixing === keyOf(row) ? null : keyOf(row));
              }}
              onApply={(row, countedQty, reason, expectedOnHand, acknowledged, expectedVersion) =>
                void applyAdjustment(row, countedQty, reason, expectedOnHand, acknowledged, expectedVersion, true)
              }
              onCancel={() => setFixing(null)}
              conflict={adjustConflict?.key === fixing ? adjustConflict : null}
              onShowHolders={(row) => void showHolders(row.sku, row.locationCode)}
              loadHolders={loadHolders}
            />
          )}

          <div className="audit-banner">
            <span>
              Stock Audit (stock-take) mode — each count goes into the stock as soon as it is saved, as an ADJUST in
              Stock History, so inbound and outbound keep running while the floor counts.
            </span>
            <HelpTip topic="Stock Audit" lines={AUDIT_HELP_LINES} />
            <span className="sp" />
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={walkLocked || (!auditOn && (visible.length === 0 || movesInFlight > 0))}
              onClick={() => (auditOn ? setExiting(true) : void startAudit())}
            >
              {auditOn ? 'Exit Stock Audit' : 'Start Stock Audit'}
            </button>
            {!auditOn && (
              <button
                type="button"
                className="btn btn-blue btn-sm"
                disabled={walkLocked || visible.length === 0 || movesInFlight > 0}
                onClick={() => void startAudit('full-count')}
              >
                Full count walk
              </button>
            )}
            {(!auditOn || isRandomCheck(auditGroupId)) && (
              <span className="random-check">
                <input
                  className="inp random-count"
                  type="text"
                  inputMode="numeric"
                  aria-label="Shelves to draw"
                  value={randomCount}
                  onChange={(event) => setRandomCount(event.target.value)}
                />
                <span>shelves</span>
                <button
                  type="button"
                  className="btn btn-blue btn-sm"
                  disabled={walkLocked || movesInFlight > 0 || countableScope.length === 0}
                  onClick={() => void startRandomCheck()}
                >
                  {auditOn ? 'Draw more shelves' : 'Random check'}
                </button>
              </span>
            )}
            <button type="button" className="btn btn-gray btn-sm" onClick={() => void openRounds()}>
              Past Audit Logs
            </button>
          </div>

          {fullCount && (
            <FullCountBand
              shelves={walkShelfList}
              current={countingShelf}
              shelfRows={currentShelfTally?.total ?? 0}
              shelfLeft={currentShelfTally?.left ?? 0}
              startedAt={walkStartedAt}
              barcodeless={barcodelessCount(sessionRows)}
              summary={fullCountSummary}
              status={walkStatus}
              choice={choiceRows}
              history={fullCountHistory}
              historyOpen={walkHistoryOpen}
              busy={walkLocked}
              onPrevious={() => goShelf(-1)}
              onNext={() => goShelf(1)}
              onPick={pickChoice}
              onToggleHistory={() => setWalkHistoryOpen((open) => !open)}
              onExport={(changesOnly) => void exportStockStatus(changesOnly)}
            />
          )}

          <div className="cs-controls">
            <CameraScanField label="Scan a shelf or product code with camera" ultimate>
              <input
                ref={searchInput.ref}
                className="inp inv-search"
                aria-label="Search SKU, product name, barcode or location"
                placeholder="Search SKU / name / barcode / location"
                autoComplete="off"
                value={query}
                onChange={(event) => {
                  searchInput.noteTyping();
                  setQuery(event.target.value);
                }}
                onClick={searchInput.reclaim}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') {
                    return;
                  }
                  event.preventDefault();
                  // Chrome on Windows reports a composing key as 229 without isComposing; the Enter that ends a
                  // composition is owed until the composed text is in the box, as asciiScanField does [SSD-7050].
                  if (event.nativeEvent.isComposing || event.keyCode === 229) {
                    searchEnterOwedRef.current = true;
                    return;
                  }
                  onSearchEnter();
                }}
                onCompositionEnd={() => {
                  if (searchEnterOwedRef.current) {
                    searchEnterOwedRef.current = false;
                    // A turn later, once the composed text has reached the search and the rows it picks are shown.
                    window.setTimeout(() => onSearchEnterRef.current(), 0);
                  }
                }}
              />
            </CameraScanField>
            <select
              className="sel"
              aria-label="Location line"
              value={line}
              onChange={(event) => setLine(event.target.value)}
            >
              <option value="all">All Locations</option>
              {locationLines(rows).map((option) => (
                <option key={option} value={option}>
                  Line {option}
                </option>
              ))}
              <option value={UNASSIGNED_LINE}>Unassigned</option>
            </select>
            <select
              className="sel"
              aria-label="Sourcing route"
              value={route}
              onChange={(event) => setRoute(event.target.value)}
            >
              <option value="all">All Sourcing Routes</option>
              {sourcingRouteOptions(rows).map((option) => (
                <option key={option} value={option}>
                  {routeLabel(option)}
                </option>
              ))}
            </select>
            <label className="zero" htmlFor="inventory-show-zero">
              <input
                id="inventory-show-zero"
                type="checkbox"
                checked={showZeroQty}
                onChange={(event) => setShowZeroQty(event.target.checked)}
              />
              Show zero-quantity rows
            </label>
            <span className="sp" />
            {holdingOrder && !auditOn && (
              <span className="blocked-note held-order">
                Edited rows stay in place. Sort again or Refresh to reorder.
              </span>
            )}
            {/* Nothing on screen keeps Start off, so the count is only said while there is something to start from. */}
            {!auditOn && query.trim() !== '' && visible.length > 0 && auditScope.length !== visible.length && (
              <span className="blocked-note">{`Start Stock Audit covers ${countableScope.length} ${
                countableScope.length === 1 ? 'row' : 'rows'
              }; the search only picks what is shown`}</span>
            )}
            {auditOn && shownRows.length !== sessionRows.length && (
              <span className="blocked-note">{`Showing ${shownRows.length} of the ${sessionRows.length} rows in this audit`}</span>
            )}
            {hiddenZeroQty > 0 && (
              <span className="blocked-note">{`${hiddenZeroQty} zero-quantity ${
                hiddenZeroQty === 1 ? 'row' : 'rows'
              } hidden`}</span>
            )}
          </div>

          {(shelfOffer !== null || foundOffers.length > 0) && (
            <div className="scan-offers">
              {shelfOffer !== null && (
                <span className="scan-offer">
                  {auditOn
                    ? `Shelf ${shelfLabel(shelfOffer)} is not in this audit.`
                    : `Shelf ${shelfLabel(shelfOffer)} is listed alone.`}{' '}
                  <button
                    type="button"
                    className="btn btn-blue btn-sm"
                    disabled={walkLocked || movesInFlight > 0}
                    onClick={() => void countShelf(shelfOffer)}
                  >
                    {auditOn ? 'Add this shelf' : 'Count this shelf'}
                  </button>
                </span>
              )}
              {countingShelf !== null &&
                foundOffers.map((sku) => (
                  <span className="scan-offer" key={sku}>
                    {`${sku} has no row on ${shelfLabel(countingShelf)}.`}{' '}
                    <button
                      type="button"
                      className="btn btn-blue btn-sm"
                      disabled={walkLocked}
                      onClick={() => void addFoundRow(sku, countingShelf)}
                    >
                      Found here — count it
                    </button>
                  </span>
                ))}
            </div>
          )}

          {fullCount && productLookup !== null && (
            <FullCountProductPanel
              code={productLookup.code}
              shelf={productLookup.shelf}
              rows={rows}
              lookupCode={async (code) => {
                const result = await adminClient.lookupBarcode(code);
                return result.found && result.sku ? result.sku : null;
              }}
              productOf={async (sku) => {
                const [product] = await stockClient.getAdminProductsBySkus([sku]);
                return product
                  ? {
                      sku: product.sku,
                      brand: product.brand ?? null,
                      name: product.name ?? null,
                      nameKor: product.nameKor ?? null,
                      size: product.size ?? null,
                      barcode: product.barcode ?? null,
                      imageUrl: product.productImageUrls?.[0] ?? null,
                      elsewhere: [],
                    }
                  : null;
              }}
              search={searchProductPicks}
              busy={walkLocked}
              onAdd={addCandidate}
              onClose={() => {
                setProductLookup(null);
                searchInput.settle();
              }}
            />
          )}

          {/* A location scanned or typed whole reads as that location's physical count [SSD-7553]. */}
          {searchShelf !== null && (
            <LocationStockCard
              rows={rows}
              locationCode={searchShelf}
              emptiesRead={showZeroQty}
              onReadEmpties={() => setShowZeroQty(true)}
            />
          )}

          {pickabilityUnreported && (
            <p className="unreported">
              Pickability is not reported by this server — Available may include virtual locations.
            </p>
          )}

          <div className={adjusting || (editingShelf && !auditOn) ? 'table-wrap stock-adjusting' : 'table-wrap'}>
            <table className="tbl fit" aria-label="Current stocks">
              {/* Laid out from these widths so every column fits the viewport: a long sku or name wraps instead of pushing the figures a scroll away [SSD-7215 follow-up]. */}
              <colgroup>
                {(auditOn ? AUDIT_COLUMN_WIDTHS : BROWSE_COLUMN_WIDTHS).map(([column, width]) => (
                  <col key={column} style={{ width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Image</th>
                  <th className="name-col">Product Name</th>
                  <th className="name-col">Product Name KR</th>
                  <th className="size-col">Size</th>
                  <th>Barcode</th>
                  <th>Sourcing Route</th>
                  <th
                    aria-sort={
                      auditOn
                        ? undefined
                        : sortMode === 'location-asc'
                          ? 'ascending'
                          : sortMode === 'location-desc'
                            ? 'descending'
                            : 'none'
                    }
                  >
                    {auditOn ? (
                      'Location'
                    ) : (
                      <button
                        type="button"
                        className="btn btn-line btn-sm"
                        onClick={() =>
                          setSortMode((mode) => (mode === 'location-asc' ? 'location-desc' : 'location-asc'))
                        }
                      >
                        Location{' '}
                        <span aria-hidden="true">
                          {sortMode === 'location-asc' ? '↑' : sortMode === 'location-desc' ? '↓' : '↕'}
                        </span>
                      </button>
                    )}
                  </th>
                  <th
                    aria-sort={
                      auditOn
                        ? undefined
                        : sortMode === 'total-desc'
                          ? 'descending'
                          : sortMode === 'total-asc'
                            ? 'ascending'
                            : 'none'
                    }
                  >
                    {auditOn ? (
                      'Total'
                    ) : (
                      <button
                        type="button"
                        className="btn btn-line btn-sm"
                        onClick={() => setSortMode((mode) => (mode === 'total-desc' ? 'total-asc' : 'total-desc'))}
                      >
                        Total{' '}
                        <span aria-hidden="true">
                          {sortMode === 'total-desc' ? '↓' : sortMode === 'total-asc' ? '↑' : '↕'}
                        </span>
                      </button>
                    )}
                  </th>
                  <th>Reserved</th>
                  <th>Available</th>
                  {auditOn && <th>Counted Qty</th>}
                  {auditOn && <th>Diff</th>}
                </tr>
              </thead>
              <tbody>
                {shownRows.map((row, index) => {
                  const key = keyOf(row);
                  const typed = counted[key] ?? '';
                  const countDocId = openRoundsByShelf[row.locationCode];
                  const countKey = countDocId ? countStateKey(countDocId, key) : null;
                  // Read off the refs that move with every keystroke and save, so the cell is drawn from the same state.
                  const frozen = countKey ? frozenRef.current[countKey] : undefined;
                  const dirty =
                    countKey !== null && isValidCount(typed) && persistedRef.current[countKey] !== typed.trim();
                  const done = applied[key];
                  // A saved row shows the gap it wrote; a row being counted shows the gap against the book it froze.
                  const diff = done && !dirty ? done.delta : countedDiff(frozen?.onHand ?? row.onHand, typed);
                  const countError = countInputError(typed);
                  const oversold = isOversold(row);
                  const stale = staleRecounts[key];
                  const short = shortfallPrompts[key];
                  const reasonPrompt = reasonPrompts[key];
                  const unassignedOnHand = isUnassignedShelf(row.locationCode)
                    ? 0
                    : (rows.find((entry) => entry.sku === row.sku && entry.locationCode === UNASSIGNED_SHELF)?.onHand ??
                      0);
                  const offersUnassigned =
                    auditOn && dirty && unassignedOnHand > 0 && Number(typed.trim()) > (frozen?.onHand ?? row.onHand);
                  const movedSinceCounted = done && !dirty ? row.onHand - done.countedQty : 0;
                  const movedSinceStarted = dirty && frozen ? row.onHand - frozen.onHand : 0;
                  const rowClass = [
                    row.pickable === false ? 'nonpickable' : '',
                    stale ? 'stale-recount' : '',
                    savedKey === key ? 'row-saved' : '',
                    auditOn && done && !dirty && !stale ? 'audit-done' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <Fragment key={key}>
                      <tr className={rowClass || undefined}>
                        <td className="num">{row.sku}</td>
                        <td>
                          {row.imageUrl ? (
                            <ProductImageZoom
                              src={row.imageUrl}
                              label={[row.brand, row.name].filter(Boolean).join(' ') || row.sku}
                            >
                              <img className="thumb" src={row.imageUrl} alt="" />
                            </ProductImageZoom>
                          ) : (
                            <div className="thumb">IMG</div>
                          )}
                        </td>
                        <td className="name-col">
                          {row.brand && <b>{row.brand} </b>}
                          <span>{row.name ?? MISSING}</span>
                          {row.productLink && (
                            <a
                              className="inv-product-link"
                              href={row.productLink}
                              target="_blank"
                              rel="noreferrer"
                              title="Open the product page on skin-seoul.com"
                              aria-label={`Open ${row.name ?? row.sku} on skin-seoul.com`}
                            >
                              <ExternalIcon />
                            </a>
                          )}
                        </td>
                        <td className="name-col">
                          <span>{row.nameKor ?? MISSING}</span>
                        </td>
                        <td className="size-col">{row.size ?? MISSING}</td>
                        <td className="num">
                          <BarcodeCell
                            sku={row.sku}
                            barcode={row.barcode}
                            productId={row.productId}
                            onSave={saveBarcode}
                          />
                        </td>
                        <td>{routesText(row)}</td>
                        <td>
                          <LocationCell
                            row={row}
                            locked={auditOn}
                            open={!auditOn && editingShelf === key}
                            // A move still out holds every ✎: reopening mid-save would offer a second move the guard then drops.
                            busy={walkLocked || movesInFlight > 0}
                            onOpen={() => {
                              setSavedKey(null);
                              setAdjusting(null);
                              setFixing(null);
                              setEditingShelf(key);
                            }}
                            onClose={() => {
                              setEditingShelf(null);
                              // Closed on a request the server never answered: the key goes, and the shelf is read again so the
                              // row shows what landed [SSD-7527].
                              if (moveKeysRef.current.delete(keyOf(row))) {
                                void load(showZeroQty);
                              }
                            }}
                            onMove={async (toLocation, units) => {
                              const outcome = await relocate(row, toLocation, units);
                              if (outcome !== 'unreachable') {
                                setEditingShelf(null);
                              }
                              return outcome === 'unreachable';
                            }}
                          />
                          {row.pickable === false && <span className="tag tag-route"> Non-pickable</span>}
                        </td>
                        <td className="num tot-cell">
                          <span className="tot-v">{row.onHand}</span>
                          {!auditOn && (
                            <button
                              type="button"
                              className="tot-edit"
                              aria-label={`Adjust stock quantity ${row.sku}`}
                              disabled={walkLocked}
                              onClick={() => {
                                setSavedKey(null);
                                setFixing(null);
                                setEditingShelf(null);
                                setAdjusting(adjusting === key ? null : key);
                              }}
                            >
                              <PencilIcon />
                            </button>
                          )}
                          {savedKey === key && <span className="tot-saved">✓ Saved</span>}
                          {adjusting === key && (
                            <StockAdjustPopover
                              row={row}
                              busy={adjustBusy}
                              onApply={(countedQty, reason, expectedOnHand, acknowledged, expectedVersion) =>
                                void applyAdjustment(
                                  row,
                                  countedQty,
                                  reason,
                                  expectedOnHand,
                                  acknowledged,
                                  expectedVersion,
                                )
                              }
                              onCancel={() => setAdjusting(null)}
                              loadHolders={loadHolders}
                              conflict={adjustConflict?.key === key ? adjustConflict : null}
                            />
                          )}
                        </td>
                        <td className="num res-qty">
                          {row.reserved > 0 ? (
                            <button
                              type="button"
                              className="reslink"
                              aria-label={`Reserved ${row.reserved}`}
                              onClick={() => void showHolders(row.sku, row.locationCode)}
                            >
                              {row.reserved}
                            </button>
                          ) : (
                            0
                          )}
                        </td>
                        {/* An oversold shelf is a book defect, so it never wears the sellable green — even at the 0 a virtual shelf reports [E-117]. */}
                        <td
                          className={oversold ? 'num av-qty neg' : 'num av-qty'}
                          title={oversold ? `Reserved ${row.reserved} exceeds ${row.onHand} on hand` : undefined}
                        >
                          {oversold ? `⚠ ${row.available}` : row.available}
                        </td>
                        {auditOn && (
                          <td>
                            <input
                              className={countError ? 'qty-in bad' : 'qty-in'}
                              type="number"
                              min="0"
                              max={MAX_COUNT}
                              step="1"
                              aria-label={`Counted quantity ${row.sku}`}
                              aria-invalid={countError !== null}
                              title={countError ?? undefined}
                              ref={(element) => {
                                countInputsRef.current[index] = element;
                              }}
                              value={typed}
                              disabled={auditBusy}
                              onBlur={(event) => {
                                // Going to the row's own Take from WH#00, reason or tick is still counting this row:
                                // those controls save it themselves, with the choice made [SSD-7447].
                                const next = event.relatedTarget;
                                if (next instanceof Node && event.currentTarget.closest('td')?.contains(next)) {
                                  return;
                                }
                                saveCount(row, false);
                              }}
                              onChange={(event) => {
                                const docId = openRoundsByShelf[row.locationCode];
                                if (docId) {
                                  const stateKey = countStateKey(docId, key);
                                  // Counting this row begins now, against the book on screen now [SSD-7447].
                                  frozenRef.current[stateKey] ??= bookOf(row);
                                  delete shortfallAckRef.current[stateKey];
                                  // A reason answers the number it was picked for; a new number asks again [SSD-7591].
                                  delete countReasonRef.current[stateKey];
                                  // A recount is sent even when it lands on the same number: it re-snapshots the shelf.
                                  if (prefilledRef.current[stateKey] !== undefined || staleRecounts[key]) {
                                    delete prefilledRef.current[stateKey];
                                    delete persistedRef.current[stateKey];
                                    delete scheduledCountsRef.current[stateKey];
                                    delete countSaveFailuresRef.current[stateKey];
                                  }
                                }
                                // Only a typed number lifts the hold: a box emptied again is still uncounted, and the
                                // round's old line must never be confirmed in its place, so emptying it before the
                                // count is written puts the hold back [SSD-7209].
                                if (staleRecounts[key] && event.target.value.trim() !== '') {
                                  liftedStaleRef.current[key] = staleRecounts[key];
                                  setStaleRecounts((standing) => {
                                    const rest = { ...standing };
                                    delete rest[key];
                                    return rest;
                                  });
                                } else if (
                                  !staleRecounts[key] &&
                                  event.target.value.trim() === '' &&
                                  liftedStaleRef.current[key]
                                ) {
                                  const held = liftedStaleRef.current[key];
                                  delete liftedStaleRef.current[key];
                                  setStaleRecounts((standing) => ({ ...standing, [key]: held }));
                                }
                                if (reasonPrompts[key]) {
                                  setReasonPrompts((standing) => {
                                    const rest = { ...standing };
                                    delete rest[key];
                                    return rest;
                                  });
                                }
                                if (shortfallPrompts[key]) {
                                  setShortfallPrompts((standing) => {
                                    const rest = { ...standing };
                                    delete rest[key];
                                    return rest;
                                  });
                                  setShortfallReasons((standing) => {
                                    const rest = { ...standing };
                                    delete rest[key];
                                    return rest;
                                  });
                                }
                                setCounted((entries) => ({ ...entries, [key]: event.target.value }));
                              }}
                              onKeyDown={(event) => onCountKeyDown(event, row, index)}
                            />
                            {fullCount && (
                              <div className="fc-row">
                                {(() => {
                                  const verdict = verdictOf(walkCountOf(row, done, Boolean(walkFound[key])));
                                  return (
                                    <span className={`status fc-verdict ${VERDICT_CLASS[verdict]}`}>
                                      {VERDICT_LABELS[verdict]}
                                    </span>
                                  );
                                })()}
                                {!done && !walkFound[key] && row.onHand > 0 && !reasonPrompt && !short && (
                                  <button
                                    type="button"
                                    className="btn btn-line btn-sm fc-none"
                                    aria-label={`Not on shelf ${row.sku}`}
                                    disabled={auditBusy}
                                    onClick={() => markNotOnShelf(row)}
                                  >
                                    Not on shelf
                                  </button>
                                )}
                                {done && !dirty && (
                                  <button
                                    type="button"
                                    className="btn btn-line btn-sm fc-recount"
                                    aria-label={`Recount ${row.sku}`}
                                    disabled={auditBusy}
                                    onClick={() => recountRow(row, index)}
                                  >
                                    Recount
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-line btn-sm fc-more-open"
                                  aria-label={`More ${row.sku}`}
                                  aria-expanded={moreRow === key}
                                  disabled={auditBusy}
                                  onClick={() => setMoreRow((open) => (open === key ? null : key))}
                                >
                                  More
                                </button>
                                {(done?.note || done?.barcodeCorrection || done?.movedFrom) && (
                                  <span className="fc-marks">
                                    {[
                                      done?.note ? `Note: ${done.note}` : '',
                                      done?.barcodeCorrection ? `Barcode should be ${done.barcodeCorrection}` : '',
                                      done?.movedFrom ? `Moved from ${shelfLabel(done.movedFrom)}` : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </span>
                                )}
                              </div>
                            )}
                            {stale && (
                              <div className="recount-note" role="alert">
                                {`Stock moved after counting began: ${
                                  stale.countedAgainst === stale.current
                                    ? `changed and back to ${stale.current}`
                                    : `${stale.countedAgainst} → ${stale.current}`
                                }${stale.changedBy ? ` by ${stale.changedBy}` : ''}. Count it again.`}
                              </div>
                            )}
                            {reasonPrompt && !short && (
                              <div className="audit-note short-note reason-note" aria-live="polite">
                                {`${reasonPrompt.countedQty} differs from the book ${reasonPrompt.book}. Pick why to save it.`}
                                <select
                                  className="sel short-reason"
                                  aria-label={`Count reason ${row.sku}`}
                                  value={typedReasons[keyOf(row)] ?? ''}
                                  disabled={auditBusy}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' && typedReasonsRef.current[keyOf(row)]) {
                                      event.preventDefault();
                                      commitTypedReason(row, true);
                                      return;
                                    }
                                    reasonKeyAtRef.current = { key: keyOf(row), at: Date.now() };
                                  }}
                                  onBlur={() => commitTypedReason(row, false)}
                                  onChange={(event) => {
                                    const picked = event.target.value as AdjustReason | '';
                                    if (!picked) {
                                      return;
                                    }
                                    // Typed: the first letter picks it while the word is still coming, so it waits for
                                    // Enter. Picked from the list: written at once.
                                    const lastKey = reasonKeyAtRef.current;
                                    reasonKeyAtRef.current = null;
                                    if (fullCount && lastKey?.key === keyOf(row) && Date.now() - lastKey.at < 200) {
                                      typedReasonsRef.current[keyOf(row)] = picked;
                                      setTypedReasons((standing) => ({ ...standing, [keyOf(row)]: picked }));
                                      return;
                                    }
                                    giveCountReason(row, picked);
                                  }}
                                >
                                  <option value="">Reason…</option>
                                  {ADJUST_REASONS.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {short && (
                              <div className="audit-note short-note" role="alert">
                                {`${short.countedQty} is below the ${short.reserved} reserved here. Going short: ${
                                  short.holders.length > 0
                                    ? short.holders
                                        .map((holder) => `#${holder.orderId}/${holder.lineNo} ×${holder.qty}`)
                                        .join(', ')
                                    : 'the orders under Reserved'
                                }.`}
                                <select
                                  className="sel short-reason"
                                  aria-label={`Short count reason ${row.sku}`}
                                  value={shortfallReasons[key] ?? ''}
                                  disabled={auditBusy}
                                  onChange={(event) => {
                                    const picked = event.target.value as AdjustReason | '';
                                    setShortfallReasons((standing) => {
                                      const rest = { ...standing };
                                      if (picked) {
                                        rest[key] = picked;
                                      } else {
                                        delete rest[key];
                                      }
                                      return rest;
                                    });
                                  }}
                                >
                                  <option value="">Reason…</option>
                                  {ADJUST_REASONS.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                                <label className="short-ack">
                                  <input
                                    type="checkbox"
                                    aria-label={`Save ${row.sku} short`}
                                    checked={false}
                                    disabled={auditBusy || !shortfallReasons[key]}
                                    onChange={() => acknowledgeShortfall(row)}
                                  />{' '}
                                  I checked these orders — save it short
                                </label>
                              </div>
                            )}
                            {offersUnassigned && (
                              <label className="audit-note take-note">
                                <input
                                  type="checkbox"
                                  aria-label={`Take ${row.sku} from WH#00`}
                                  checked={!keepUnassigned[key]}
                                  onChange={(event) => {
                                    const keep = !event.target.checked;
                                    // The ref first, so the save below already carries the choice.
                                    keepUnassignedRef.current = { ...keepUnassignedRef.current, [key]: keep };
                                    setKeepUnassigned((standing) => ({ ...standing, [key]: keep }));
                                    saveCount(row, false);
                                  }}
                                />{' '}
                                {`Take from WH#00 (${unassignedOnHand} there)`}
                              </label>
                            )}
                            {movedSinceStarted !== 0 && !stale && (
                              <div className="audit-note moving-note">
                                {`Stock moved ${movedSinceStarted > 0 ? '+' : ''}${movedSinceStarted} since you started — saving asks for a recount.`}
                              </div>
                            )}
                            {auditOn && done && !dirty && !stale && (
                              <div className="audit-note applied-note">
                                {`✓ In stock${done.movedFromUnassigned > 0 ? ` · ${done.movedFromUnassigned} from WH#00` : ''}${
                                  movedSinceCounted !== 0
                                    ? ` · moved ${movedSinceCounted > 0 ? '+' : ''}${movedSinceCounted} since`
                                    : ''
                                }`}
                              </div>
                            )}
                          </td>
                        )}
                        {auditOn && (
                          <td className={`num ${diff !== null && diff < 0 ? 'diff-neg' : 'diff-pos'}`}>
                            {diff === null ? MISSING : diff > 0 ? `+${diff}` : diff}
                          </td>
                        )}
                      </tr>
                      {fullCount && moreRow === key && (
                        <tr className="fc-more-row">
                          <td colSpan={13}>
                            <WalkRowActions
                              row={row}
                              counted={Boolean(done)}
                              canSwap={!walkFound[key] || Boolean(done)}
                              checked={Boolean(done?.barcodeCheck)}
                              hasBarcode={Boolean(row.barcode?.trim())}
                              note={done?.note ?? ''}
                              correction={done?.barcodeCorrection ?? ''}
                              otherShelves={walkShelfList.filter((shelf) => shelf !== row.locationCode)}
                              busy={auditBusy}
                              onCheckByName={(reason) =>
                                void annotateRow(
                                  row,
                                  { barcodeCheck: { method: 'NAME', reason } },
                                  'confirmed by name.',
                                )
                              }
                              onSaveNote={(text) =>
                                void annotateRow(row, { note: text }, text ? 'note saved.' : 'note cleared.')
                              }
                              onSaveCorrection={(code) =>
                                void annotateRow(
                                  row,
                                  { barcodeCorrection: code },
                                  code
                                    ? `barcode correction ${code} saved for the catalog team.`
                                    : 'correction cleared.',
                                )
                              }
                              onSwap={(foundSku, foundQty, reason) => void swapRow(row, foundSku, foundQty, reason)}
                              onMove={(toShelf) => void moveRow(row, toShelf)}
                              onClose={() => setMoreRow(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {shownRows.length === 0 && (
                  <tr>
                    <td colSpan={auditOn ? 13 : 11} className="blocked-note">
                      {loading ? (
                        'Loading the stock…'
                      ) : hasActiveFilters ? (
                        <>
                          No products match these filters{' '}
                          <button type="button" className="btn btn-line btn-sm" onClick={clearFilters}>
                            Clear filters
                          </button>
                        </>
                      ) : (
                        'No stock is available.'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {auditOn && (
            <div className="auditsummary">
              <span>{`Counted ${countedRows} of ${sessionRows.length} rows · ${rowsLeft} left`}</span>
              {shownShelf && <span>{`Shelf ${shelfLabel(shownShelf.locationCode)}: ${shownShelf.left} left`}</span>}
              <span>{`In the stock: ${tally.adjustments} ${tally.adjustments === 1 ? 'adjustment' : 'adjustments'} (−${
                tally.decreases
              } / +${tally.increases})`}</span>
              {/* Every count is in the stock already, so finishing closes the shelves and names the rows left uncounted. */}
              <button
                type="button"
                className="btn btn-blue btn-sm"
                disabled={auditBusy}
                onClick={() => {
                  if (hasInvalidCounts) {
                    showError(`Fix the highlighted counts. ${COUNT_ERROR}`, undefined, AUTO_DISMISS);
                    return;
                  }
                  const waiting = holdCountsWithoutReason();
                  if (waiting > 0) {
                    showError(
                      `${waiting === 1 ? '1 count differs' : `${waiting} counts differ`} from the book without a reason. Pick why on ${
                        waiting === 1 ? 'that row' : 'those rows'
                      }, then finish.`,
                      undefined,
                      AUTO_DISMISS,
                    );
                    return;
                  }
                  setFinishingAudit(true);
                }}
              >
                Finish Audit
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <StockHistoryPane
          stockClient={stockClient}
          adminClient={adminClient}
          stockedRows={rows}
          onFailure={(text) => showError(text)}
          addressed={historySearch}
          onSearch={(search) => showInAddress('history', search)}
        />
      )}

      {(tab === 'inbound' || tab === 'outbound') && (
        <MovementFormPane
          key={tab}
          direction={tab}
          stockClient={stockClient}
          adminClient={adminClient}
          operator={operator}
          onRecorded={(text) => showSuccess(text)}
          onFailure={(text) => showError(text)}
        />
      )}

      {reservedSku && (
        <ReservedOrdersModal
          sku={reservedSku}
          locationCode={reservedLocation}
          holders={holders}
          loading={holdersLoading}
          failed={holdersFailed}
          draft={releaseDraft}
          busyKey={releaseBusy}
          oversold={shortSkus.includes(reservedSku)}
          onDraftChange={setReleaseDraft}
          onRelease={(holder) => void release(holder)}
          onRetry={() => void showHolders(reservedSku, reservedLocation)}
          onClose={() => {
            reservedRequestRef.current += 1;
            reservedSkuRef.current = null;
            setReservedSku(null);
          }}
        />
      )}

      {showRounds && (
        <PastAuditLogsModal
          rounds={rounds}
          loading={roundsLoading}
          origin={roundsOrigin}
          hasMore={roundsCursor !== null}
          onOriginChange={filterRounds}
          onLoadMore={() => void loadRounds(roundsOrigin, roundsCursor)}
          onCloseOpen={(shelves) => void closeOpenRounds(shelves)}
          ownDocIds={new Set(Object.values(openRoundsByShelf))}
          closing={closingRounds}
          notifications={<FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />}
          onClose={closeRounds}
        />
      )}

      {importingLocations && (
        <LocationImportModal
          client={adminClient}
          stockClient={stockClient}
          onClose={() => setImportingLocations(false)}
          onApplied={() => void load(showZeroQty)}
        />
      )}

      {finishingAudit && (
        <FinishAuditModal
          tally={tally}
          counted={countedRows}
          shelvesLeft={shelvesLeft.filter((shelf) => shelf.left > 0)}
          busy={auditBusy}
          onFinish={() => void finishAudit()}
          notifications={<FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />}
          onClose={() => setFinishingAudit(false)}
          randomCheck={isRandomCheck(auditGroupId)}
        />
      )}

      {exiting && (
        <AbandonAuditModal
          shelves={Object.keys(openRoundsByShelf).length}
          busy={auditBusy}
          onAbandon={() => void abandonAudit()}
          onClose={() => setExiting(false)}
        />
      )}

      {!finishingAudit && !showRounds && <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />}
    </div>
  );
}
